import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { preferredTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';
import { Link } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { GoalPlanner } from '../../../components/coaching/GoalPlanner';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { parseGoalPlanImport } from '../../../utils/goalPlanImport';
import { goalsApi, sectionsApi } from '../../../services/apiV2';
import {
  DailyGoalCycle,
  ExternalWorkEvent,
  GoalAiAssistEvent,
  GoalMilestone,
  InstructionalCycle,
  GoalTeacherComment,
  Section,
  StudentPrimaryGoal,
  WeeklyGoalCheckpoint,
} from '../../../types/v2';

type SectionDailyRow = {
  student_id: number;
  student_name: string;
  status: 'not_checked_in' | 'checked_in' | 'checked_out' | 'not_applicable';
  goals_applicable?: boolean;
  is_school_day?: boolean;
  grade_level?: string;
  checked_in_at?: string | null;
  checked_out_at?: string | null;
  bypass_active: boolean;
  missing_internal_count: number;
  missing_external_count: number;
  missing_total_count: number;
  cycle_id?: number | null;
  plan_target?: string;
  plan_success_criteria?: string;
  protected_goal_minutes?: number | null;
  planned_secondary_work?: string;
  planned_work_mode?: string;
  active_work_block?: {
    id?: number;
    title?: string;
    planned_minutes?: number | null;
    actual_minutes?: number;
    started_at?: string;
    track_key?: string;
  } | null;
  focus_state?: {
    state: 'not_started' | 'active' | 'paused' | 'reset_due' | 'finished';
    label: string;
    elapsed_seconds?: number;
    remaining_seconds?: number | null;
    block?: {
      id: number;
      title?: string;
      status?: string;
      planned_minutes?: number | null;
      actual_minutes?: number;
      started_at?: string;
      track_key?: string;
    } | null;
    support?: {
      status: 'requested' | 'acknowledged';
      requested_at?: string | null;
      acknowledged_at?: string | null;
      block_id: number;
    } | null;
  };
  support_request?: {
    status: 'requested' | 'acknowledged';
    requested_at?: string | null;
    acknowledged_at?: string | null;
    block_id: number;
  } | null;
  support_requested?: boolean;
  project_plan_target?: string;
  project_plan_success_criteria?: string;
  current_focus?: {
    key?: string;
    label?: string;
    title?: string;
    secondary_title?: string;
    origin?: string;
  };
  work_status?: string;
  priority_result?: string;
  pathway_evidence?: { description?: string; url?: string };
  project_result?: string;
  project_evidence?: { description?: string; url?: string; portfolio_candidate?: boolean };
  reflection_status?: 'yes' | 'partial' | 'no' | '';
  reflection_text?: string;
  reflection_next_step?: string;
  plan_change_reason?: string;
  plan_change_note?: string;
  completion_rating?: number | null;
  confidence_rating?: number | null;
  moved_toward_primary_goal?: boolean | null;
  external_risks?: {
    active_block: boolean;
  };
  goal_quality?: GoalQualitySummary | null;
  first_step_text?: string;
  triage?: TriageSummary;
  followup_triage?: TriageSummary;
  project_work?: ProjectWorkSummary;
  primary_goal?: StudentPrimaryGoal | null;
  weekly_checkpoint?: WeeklyGoalCheckpoint | null;
  week_start_date?: string;
  weekly_checkpoint_availability?: {
    is_due_or_past?: boolean;
    is_available?: boolean;
    due_date?: string;
  };
};

type GoalQuality = {
  label: 'Strong' | 'Needs Clarification' | 'Weak / Vague' | 'Missing' | string;
  level: 'strong' | 'needs_clarification' | 'weak' | 'missing' | string;
  score: number;
  reasons: string[];
};

type GoalQualitySummary = {
  learning?: GoalQuality;
  completion?: GoalQuality;
};

type TriageSummary = {
  label: 'On Track' | 'Monitor' | 'Needs Attention' | 'Watch' | 'At Risk' | 'Not Applicable' | string;
  level: 'on_track' | 'monitor' | 'needs_attention' | 'watch' | 'at_risk' | 'not_applicable' | string;
  rank: number;
  reasons: string[];
  recommended_action?: string;
};

type ProjectWorkSummary = {
  has_project_work: boolean;
  role?: '' | 'main_project' | 'secondary_project' | string;
  role_label?: string;
  selected_title?: string;
  selected_kind?: string;
  selected_source?: string;
  project_block_count?: number;
  project_minutes?: number;
  active_project_block?: boolean;
  evidence_count?: number;
  heavy_missing_with_project?: boolean;
};

type ExternalWorkSummary = {
  total_blocks: number;
  active_blocks: number;
  completed_blocks: number;
  switch_count: number;
  last_event_type: string;
  last_event_at: string | null;
};

type SectionMissingQueueItem = {
  id: number;
  kind: 'internal_assignment' | 'external_task';
  title: string;
  student_id: number;
  student_name: string;
  due_at?: string | null;
  due_date?: string | null;
  overdue: boolean;
  due_today: boolean;
  in_grace?: boolean;
  priority_rank_global?: number;
};

type SchoolCalendarContext = {
  academic_year: string;
  is_school_wide: boolean;
  is_school_day: boolean;
  is_closure: boolean;
  closure_label: string;
  first_school_day?: string;
  last_school_day?: string;
  source_url?: string;
};

type TeacherPriorityLevel = 'needs_attention' | 'monitor' | 'on_track' | 'not_applicable';
type RosterFilter = 'all' | 'followup' | 'missing_priority' | 'reviews_due' | 'wrapups' | 'help';
type GoalMode = 'off' | 'basic' | 'guided' | 'full';
type CycleKind = InstructionalCycle['cycle_kind'];
type CycleTransition = 'activate' | 'review' | 'finalize';
type GoalSupportMode = 'adapted' | 'teacher_stated';
type GoalPlanImportScope = 'daily_plan_only' | 'primary_goal_only' | 'primary_goal_and_daily_plan';

type PrimaryGoalSupportDraft = {
  support_mode: GoalSupportMode;
  student_visible_rationale: string;
  goal_purpose: 'technical_cte' | 'personal_professional' | '';
  title: string;
  why_text: string;
  target_metric_text: string;
};

type DailyPlanSupportDraft = {
  support_mode: GoalSupportMode;
  student_visible_rationale: string;
  plan_track: string;
  plan_target: string;
  plan_first_step: string;
  plan_success_criteria: string;
  protected_goal_minutes: string;
  planned_secondary_work: string;
  planned_work_mode: string;
};

type CycleDraft = {
  title: string;
  cycle_kind: CycleKind;
  audience_grades: string[];
  start_date: string;
  end_date: string;
  sequence: string;
};

type GoalPolicyPreset = 'legacy' | 'grades_6_8' | 'grades_9_10' | 'grades_11_12';

const PRIORITY_GROUPS: Array<{ level: TeacherPriorityLevel; label: string; description: string }> = [
  { level: 'needs_attention', label: 'Needs Attention', description: 'Start here.' },
  { level: 'monitor', label: 'Monitor', description: 'Check soon.' },
  { level: 'on_track', label: 'On Track', description: 'Okay for now.' },
  { level: 'not_applicable', label: 'Not Applicable', description: 'Goals disabled or not scheduled.' },
];

const todayISO = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const emptyCycleDraft = (audienceGrades: string[] = []): CycleDraft => ({
  title: '',
  cycle_kind: 'performance_period',
  audience_grades: audienceGrades,
  start_date: '',
  end_date: '',
  sequence: '1',
});

const primaryGoalSupportDraft = (goal?: StudentPrimaryGoal | null): PrimaryGoalSupportDraft => ({
  support_mode: goal ? 'adapted' : 'teacher_stated',
  student_visible_rationale: '',
  goal_purpose: goal?.goal_purpose || '',
  title: String(goal?.title || ''),
  why_text: String(goal?.why_text || ''),
  target_metric_text: String(goal?.target_metric_text || ''),
});

const dailyPlanSupportDraft = (cycle?: DailyGoalCycle | null): DailyPlanSupportDraft => ({
  support_mode: cycle ? 'adapted' : 'teacher_stated',
  student_visible_rationale: '',
  plan_track: String(cycle?.plan_track || ''),
  plan_target: String(cycle?.plan_target || ''),
  plan_first_step: String(cycle?.plan_first_step || ''),
  plan_success_criteria: String(cycle?.plan_success_criteria || ''),
  protected_goal_minutes: cycle?.protected_goal_minutes ? String(cycle.protected_goal_minutes) : '',
  planned_secondary_work: String(cycle?.planned_secondary_work || ''),
  planned_work_mode: String(cycle?.planned_work_mode || ''),
});

const cycleKindLabel = (kind: CycleKind) => {
  if (kind === 'performance_period') return 'Performance period';
  if (kind === 'transition_review') return 'Transition / review';
  return 'Instructional period';
};

const findBlockingActiveCycle = (
  cycle: InstructionalCycle,
  cycles: InstructionalCycle[],
) => cycles.find((candidate) => {
  if (candidate.id === cycle.id || candidate.state !== 'active') return false;
  if ((candidate.parent ?? null) !== (cycle.parent ?? null)) return false;
  const candidateAudience = candidate.audience_grades || [];
  const cycleAudience = cycle.audience_grades || [];
  return candidateAudience.length === 0
    || cycleAudience.length === 0
    || candidateAudience.some((grade) => cycleAudience.includes(grade));
});

const apiErrorMessage = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const message = typeof detail.message === 'string' ? detail.message : '';
    if (message) return message;
    const firstValue = Object.values(detail)[0];
    if (Array.isArray(firstValue) && firstValue.length > 0) return String(firstValue[0]);
    if (typeof firstValue === 'string') return firstValue;
  }
  return String(err?.response?.data?.error || fallback);
};

const normalizeArray = (data: any) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const isGoalsSectionActive = (section: Section) => (
  Boolean(section?.goal_policy?.goals_available ?? section?.goals_feature_enabled)
  && String(section?.goal_mode || 'off') !== 'off'
);

const priorityLevelForTriage = (triage?: TriageSummary | null): TeacherPriorityLevel => {
  if (triage?.level === 'needs_attention' || triage?.level === 'at_risk') return 'needs_attention';
  if (triage?.level === 'monitor' || triage?.level === 'watch') return 'monitor';
  if (triage?.level === 'not_applicable') return 'not_applicable';
  return 'on_track';
};

const priorityLabelForLevel = (level: TeacherPriorityLevel) => (
  PRIORITY_GROUPS.find((group) => group.level === level)?.label || 'On Track'
);

const priorityReason = (triage?: TriageSummary | null) => (
  String((triage?.reasons || [])[0] || 'No major goal or progress risks detected.')
);

const dailyStatusLabel = (row: SectionDailyRow) => {
  if (row.is_school_day === false) return 'not scheduled';
  return row.status.replace(/_/g, ' ');
};

const goalCycleCompatibility = (row: SectionDailyRow, cycle: InstructionalCycle) => {
  const goal = row.primary_goal;
  if (!goal) return { eligible: false, reason: 'No active primary goal.' };
  if (goal.instructional_cycle === cycle.id) return { eligible: true, reason: '' };
  if (goal.instructional_cycle) return { eligible: false, reason: 'Connected to another cycle.' };
  if (cycle.audience_grades.length > 0 && !cycle.audience_grades.includes(String(row.grade_level || ''))) {
    return { eligible: false, reason: `Grade ${row.grade_level || 'unknown'} is outside this cycle audience.` };
  }
  if (!goal.start_date || !goal.end_date) return { eligible: false, reason: 'Goal dates are incomplete.' };
  if (goal.start_date < cycle.start_date || goal.end_date > cycle.end_date) {
    return { eligible: false, reason: `Goal dates ${goal.start_date} to ${goal.end_date} do not fit inside this cycle.` };
  }
  return { eligible: true, reason: '' };
};

const priorityLevelForRow = (row: SectionDailyRow): TeacherPriorityLevel => {
  if (row.support_requested) return 'needs_attention';
  if (row.followup_triage) return priorityLevelForTriage(row.followup_triage);
  if (row.triage) return priorityLevelForTriage(row.triage);
  if (!row.goals_applicable || row.status === 'not_applicable') return 'not_applicable';
  if (row.status === 'not_checked_in') return 'needs_attention';
  if (Number(row.missing_total_count || 0) >= 4) return 'needs_attention';
  if (row.external_risks?.active_block) return 'monitor';
  if (Number(row.missing_total_count || 0) > 0) return 'monitor';
  return 'on_track';
};

const priorityReasonForRow = (row: SectionDailyRow) => {
  if (row.support_request?.status === 'acknowledged') return 'Help request acknowledged; support is still in progress.';
  if (row.support_requested) return 'Student requested help with the current task.';
  const triageReason = String(((row.followup_triage || row.triage)?.reasons || [])[0] || '').trim();
  if (triageReason) return triageReason;
  if (!row.goals_applicable || row.status === 'not_applicable') return 'Goals are not enabled for this student.';
  if (row.status === 'not_checked_in') return 'Student has not checked in.';
  if (Number(row.missing_total_count || 0) >= 4) return 'High missing-work load.';
  if (row.external_risks?.active_block) return 'External work block is active.';
  if (Number(row.missing_total_count || 0) > 0) return 'Missing work is open.';
  return 'No major goal or progress risks detected.';
};

const hasGoalQualityWarning = (row: SectionDailyRow) => (
  [row.goal_quality?.learning?.level, row.goal_quality?.completion?.level]
    .some((level) => Boolean(level) && level !== 'strong')
);

const teacherNextMoveForRow = (row: SectionDailyRow) => {
  if (row.support_request?.status === 'acknowledged') return 'Follow up, then mark the request resolved.';
  if (row.support_requested) return 'Check the current blocker and agree on one next move.';
  const recommendedAction = String((row.followup_triage || row.triage)?.recommended_action || '').trim();
  if (recommendedAction) return recommendedAction;
  if (row.status === 'not_checked_in') return 'Review the missing daily update.';
  if (row.primary_goal && !row.weekly_checkpoint) return 'Review the missing weekly checkpoint.';
  if (hasGoalQualityWarning(row)) return 'Clarify the goal definition with the student.';
  if (Number(row.missing_total_count || 0) > 0) return 'Check the goal against current missing work.';
  return 'Review current progress and decide whether a check-in is needed.';
};

const wrapUpResultLabel = (row: Pick<SectionDailyRow, 'priority_result' | 'reflection_status'>) => {
  const result = String(row.priority_result || row.reflection_status || '').toLowerCase();
  const labels: Record<string, string> = {
    met: 'Met',
    partially_met: 'Partially met',
    blocked: 'Blocked',
    changed_authentic_work: 'Changed authentic work',
    ready_for_review: 'Ready for review',
    yes: 'Met',
    partial: 'Partially met',
    no: 'Not met',
  };
  return labels[result] || 'Submitted';
};

const wrapUpResultClass = (row: SectionDailyRow) => {
  const result = String(row.priority_result || row.reflection_status || '').toLowerCase();
  if (['met', 'yes', 'ready_for_review'].includes(result)) return 'bg-emerald-100 text-emerald-800';
  if (['partially_met', 'partial', 'changed_authentic_work'].includes(result)) return 'bg-amber-100 text-amber-800';
  if (['blocked', 'no'].includes(result)) return 'bg-rose-100 text-rose-800';
  return 'bg-slate-100 text-slate-700';
};

const dailyReportCheckOut = (row: SectionDailyRow) => {
  if (row.status !== 'checked_out') return row.status === 'not_applicable' ? 'Not scheduled' : 'Missing check-out';
  const parts = [
    wrapUpResultLabel(row),
    row.reflection_text,
    row.pathway_evidence?.description ? `Evidence: ${row.pathway_evidence.description}` : '',
    row.plan_change_reason ? `Plan: ${row.plan_change_reason.replace(/_/g, ' ')}` : '',
    row.plan_change_note ? `Note: ${row.plan_change_note}` : '',
  ].filter(Boolean);
  return parts.join(' | ');
};

const dailyReportNextStep = (row: SectionDailyRow) => {
  if (row.reflection_next_step?.trim()) return row.reflection_next_step.trim();
  if (row.status === 'checked_out') return 'No next step recorded';
  if (row.status === 'checked_in') return 'Complete check-out and name the next step';
  if (row.status === 'not_applicable') return 'Not scheduled';
  return 'Complete check-in and set today\'s plan';
};

const rowHasMissingPriority = (row: SectionDailyRow) => Boolean(
  row.goals_applicable
  && row.is_school_day !== false
  && (row.status === 'not_checked_in' || (row.status !== 'not_applicable' && !row.plan_target?.trim()))
);

const rowHasReviewDue = (row: SectionDailyRow) => Boolean(
  row.primary_goal?.instructional_cycle_detail?.state === 'review'
  || (
    row.primary_goal
    && row.weekly_checkpoint_availability?.is_due_or_past
    && !row.weekly_checkpoint
  )
);

const checkpointLabelForRow = (row: SectionDailyRow) => {
  if (row.weekly_checkpoint) return `Weekly checkpoint ${row.weekly_checkpoint.confidence_rating}/5`;
  if (row.weekly_checkpoint_availability?.is_due_or_past) return 'Weekly checkpoint due';
  return row.weekly_checkpoint_availability?.due_date
    ? `Checkpoint due ${row.weekly_checkpoint_availability.due_date}`
    : 'Checkpoint not yet due';
};

const GRADE_OPTIONS = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];
const GOAL_MODE_OPTIONS: Array<{ value: GoalMode; label: string; description: string }> = [
  { value: 'off', label: 'Off', description: 'Hide goal system for this section.' },
  { value: 'basic', label: 'Basic', description: 'Primary goal and daily alignment.' },
  { value: 'guided', label: 'Guided', description: 'Add weekly checkpoints.' },
  { value: 'full', label: 'Full', description: 'Add final reflection and full triage.' },
];
const GOAL_POLICY_PRESETS: Array<{ value: GoalPolicyPreset; label: string; description: string }> = [
  { value: 'legacy', label: 'Custom / current settings', description: 'Keep the existing mode and grade controls.' },
  { value: 'grades_6_8', label: 'Grades 6–8', description: 'Goals off.' },
  { value: 'grades_9_10', label: 'Grades 9–10', description: '10-week goal structure; no daily priority or hard gate.' },
  { value: 'grades_11_12', label: 'Grades 11–12', description: 'Required daily priority with reminders only; never a hard gate.' },
];

export const V2TeacherGoals: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_goals');
  const [allSections, setAllSections] = useState<Section[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  useEffect(() => { rememberTeacherSection(selectedSectionId); }, [selectedSectionId]);
  const [selectedDate, setSelectedDate] = useState<string>(todayISO());
  const [rows, setRows] = useState<SectionDailyRow[]>([]);

  const [rosterFilter, setRosterFilter] = useState<RosterFilter>('all');
  const [workModeFilter, setWorkModeFilter] = useState('all');
  const [schoolCalendar, setSchoolCalendar] = useState<SchoolCalendarContext | null>(null);
  const [missingQueueItems, setMissingQueueItems] = useState<SectionMissingQueueItem[]>([]);
  const [missingQueueTotal, setMissingQueueTotal] = useState(0);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [studentDetail, setStudentDetail] = useState<{
    cycle: DailyGoalCycle | null;
    comments: GoalTeacherComment[];
    history: DailyGoalCycle[];
    externalWorkSummary: ExternalWorkSummary;
    externalWorkEvents: ExternalWorkEvent[];
    aiAssistEvents: GoalAiAssistEvent[];
    goalQuality: GoalQualitySummary | null;
    triage: TriageSummary | null;
    followupTriage: TriageSummary | null;
    projectWork: ProjectWorkSummary | null;
    externalRisks: SectionDailyRow['external_risks'] | null;
    currentPrimaryGoal: StudentPrimaryGoal | null;
    activeGoals: StudentPrimaryGoal[];
    activePrimaryGoal: StudentPrimaryGoal | null;
    currentWeeklyCheckpoint: WeeklyGoalCheckpoint | null;
    weeklyCheckpoints: WeeklyGoalCheckpoint[];
    weekStartDate: string;
  } | null>(null);

  const [commentText, setCommentText] = useState('');
  const [bypassReasonByStudent, setBypassReasonByStudent] = useState<Record<number, string>>({});
  const [goalReviewNotes, setGoalReviewNotes] = useState<Record<number, string>>({});
  const [goalReviewSaving, setGoalReviewSaving] = useState(false);
  const [goalReviewMessage, setGoalReviewMessage] = useState('');
  const [primarySupportDraft, setPrimarySupportDraft] = useState<PrimaryGoalSupportDraft>(() => primaryGoalSupportDraft());
  const [dailySupportDraft, setDailySupportDraft] = useState<DailyPlanSupportDraft>(() => dailyPlanSupportDraft());
  const [goalSupportSaving, setGoalSupportSaving] = useState<'primary' | 'daily' | ''>('');
  const [goalSupportError, setGoalSupportError] = useState('');
  const [goalSupportSaved, setGoalSupportSaved] = useState('');
  const [goalPlanImportSource, setGoalPlanImportSource] = useState('');
  const [goalPlanImportScope, setGoalPlanImportScope] = useState<GoalPlanImportScope>('daily_plan_only');
  const [workspaceView, setWorkspaceView] = useState<'daily' | 'goals' | 'report' | 'settings'>('daily');
  const [detailTab, setDetailTab] = useState<'today' | 'goal' | 'planner' | 'history' | 'support'>('today');
  const [confirmGoalChange, setConfirmGoalChange] = useState(false);
  const [goalPlanImportMessage, setGoalPlanImportMessage] = useState('');
  const [goalPlanImportWarnings, setGoalPlanImportWarnings] = useState<string[]>([]);
  const pendingImportedPrimaryDraftRef = useRef(false);
  const pendingImportedDailyDraftRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [supportActionLoading, setSupportActionLoading] = useState('');
  const [sectionGoalsEnabled, setSectionGoalsEnabled] = useState(false);
  const [sectionGoalMode, setSectionGoalMode] = useState<GoalMode>('off');
  const [sectionGoalGrades, setSectionGoalGrades] = useState<string[]>([]);
  const [sectionGoalPreset, setSectionGoalPreset] = useState<GoalPolicyPreset>('legacy');
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [instructionalCycles, setInstructionalCycles] = useState<InstructionalCycle[]>([]);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [cycleSaving, setCycleSaving] = useState(false);
  const [goalCycleSavingId, setGoalCycleSavingId] = useState<number | null>(null);
  const [cycleError, setCycleError] = useState('');
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);
  const [cycleDraft, setCycleDraft] = useState<CycleDraft>(() => emptyCycleDraft());
  const [loadedRosterContext, setLoadedRosterContext] = useState<{ sectionId: number; date: string } | null>(null);
  const sectionDailyRequestIdRef = useRef(0);
  const studentDetailRequestIdRef = useRef(0);
  const studentDetailRef = useRef<HTMLDivElement>(null);
  const studentDetailTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [detailNavigationStudentId, setDetailNavigationStudentId] = useState<number | null>(null);

  const selectedStudentRow = useMemo(
    () => rows.find((row) => row.student_id === selectedStudentId) || null,
    [rows, selectedStudentId],
  );
  const activeSections = useMemo(
    () => allSections.filter(isGoalsSectionActive),
    [allSections],
  );
  const selectedSection = useMemo(
    () => allSections.find((section) => section.id === selectedSectionId) || null,
    [allSections, selectedSectionId],
  );
  const selectedSectionHasGoalsActive = useMemo(
    () => Boolean(selectedSection && isGoalsSectionActive(selectedSection)),
    [selectedSection],
  );
  const triageGroups = useMemo(() => (
    PRIORITY_GROUPS.map((group) => ({
      ...group,
      rows: rows.filter((row) => priorityLevelForRow(row) === group.level),
    })).filter((group) => group.rows.length > 0)
  ), [rows]);
  const triageCounts = useMemo(() => (
    rows.reduce<Record<TeacherPriorityLevel, number>>((counts, row) => {
      const level = priorityLevelForRow(row);
      return {
        ...counts,
        [level]: counts[level] + 1,
      };
    }, {
      needs_attention: 0,
      monitor: 0,
      on_track: 0,
      not_applicable: 0,
    })
  ), [rows]);
  const attentionRows = useMemo(() => (
    rows
      .filter((row) => ['needs_attention', 'monitor'].includes(priorityLevelForRow(row)))
      .sort((left, right) => {
        const levelRank = { needs_attention: 0, monitor: 1, on_track: 2, not_applicable: 3 };
        const levelDelta = levelRank[priorityLevelForRow(left)] - levelRank[priorityLevelForRow(right)];
        if (levelDelta !== 0) return levelDelta;
        const leftRank = Number((left.followup_triage || left.triage)?.rank ?? Number.MAX_SAFE_INTEGER);
        const rightRank = Number((right.followup_triage || right.triage)?.rank ?? Number.MAX_SAFE_INTEGER);
        if (leftRank !== rightRank) return leftRank - rightRank;
        return left.student_name.localeCompare(right.student_name);
      })
  ), [rows]);
  const wrapUpRows = useMemo(
    () => rows.filter((row) => row.goals_applicable && row.status === 'checked_out'),
    [rows],
  );
  const wrapUpPendingRows = useMemo(
    () => rows.filter((row) => row.goals_applicable && row.status !== 'checked_out' && row.status !== 'not_applicable'),
    [rows],
  );
  const dailyReportCounts = useMemo(() => ({
    applicable: rows.filter((row) => row.goals_applicable && row.status !== 'not_applicable').length,
    checkedIn: rows.filter((row) => row.goals_applicable && ['checked_in', 'checked_out'].includes(row.status)).length,
    checkedOut: rows.filter((row) => row.goals_applicable && row.status === 'checked_out').length,
    missingNextStep: rows.filter((row) => row.goals_applicable && row.status === 'checked_out' && !row.reflection_next_step?.trim()).length,
  }), [rows]);
  const rosterFilterCounts = useMemo<Record<RosterFilter, number>>(() => ({
    all: rows.length,
    followup: rows.filter((row) => ['needs_attention', 'monitor'].includes(priorityLevelForRow(row))).length,
    missing_priority: rows.filter(rowHasMissingPriority).length,
    reviews_due: rows.filter(rowHasReviewDue).length,
    wrapups: rows.filter((row) => row.status === 'checked_out').length,
    help: rows.filter((row) => row.support_requested).length,
  }), [rows]);
  const filteredRosterRows = useMemo(() => rows.filter((row) => {
    if (workModeFilter !== 'all' && row.planned_work_mode !== workModeFilter) return false;
    if (rosterFilter === 'followup') return ['needs_attention', 'monitor'].includes(priorityLevelForRow(row));
    if (rosterFilter === 'missing_priority') return rowHasMissingPriority(row);
    if (rosterFilter === 'reviews_due') return rowHasReviewDue(row);
    if (rosterFilter === 'wrapups') return row.status === 'checked_out';
    if (rosterFilter === 'help') return Boolean(row.support_requested);
    return true;
  }).sort((left, right) => {
    const rank = { needs_attention: 0, monitor: 1, on_track: 2, not_applicable: 3 };
    return rank[priorityLevelForRow(left)] - rank[priorityLevelForRow(right)]
      || left.student_name.localeCompare(right.student_name);
  }), [rosterFilter, rows, workModeFilter]);
  const goalAttentionSummary = useMemo(() => {
    if (schoolCalendar?.is_school_wide && !schoolCalendar.is_school_day) {
      return {
        missingDailyUpdates: 0,
        offTarget: 0,
        checkInsOrReviews: 0,
        qualityWarnings: 0,
      };
    }
    const dateBoundary = new Date(`${selectedDate}T23:59:59`);
    const milestoneNeedsReview = (row: SectionDailyRow) => (row.primary_goal?.milestones || []).some((milestone) => {
      const status = String(milestone.status || '').toLowerCase();
      if (['blocked', 'overdue'].includes(status)) return true;
      if (!milestone.due_date || ['completed', 'verified'].includes(status)) return false;
      const dueDate = new Date(`${milestone.due_date}T23:59:59`);
      return !Number.isNaN(dueDate.getTime()) && dueDate <= dateBoundary;
    });
    return {
      missingDailyUpdates: rows.filter((row) => row.goals_applicable && row.status === 'not_checked_in').length,
      offTarget: rows.filter((row) => (
        priorityLevelForRow(row) === 'needs_attention'
        || milestoneNeedsReview(row)
      )).length,
      checkInsOrReviews: rows.filter((row) => (
        Boolean(row.primary_goal && !row.weekly_checkpoint)
        || String(row.primary_goal?.instructional_cycle_detail?.state || '') === 'review'
        || milestoneNeedsReview(row)
      )).length,
      qualityWarnings: rows.filter(hasGoalQualityWarning).length,
    };
  }, [rows, schoolCalendar, selectedDate]);

  const loadSections = async () => {
    const response = await sectionsApi.list();
    const sectionRows = normalizeArray(response.data).filter((section: Section) => section.is_active !== false);
    setAllSections(sectionRows);
  };

  const loadInstructionalCycles = async (sectionId: number) => {
    setCyclesLoading(true);
    setCycleError('');
    try {
      const payload = await goalsApi.instructionalCycles.list(sectionId);
      setInstructionalCycles(normalizeArray(payload) as InstructionalCycle[]);
    } catch (err: any) {
      setInstructionalCycles([]);
      setCycleError(apiErrorMessage(err, 'Failed to load instructional cycles.'));
    } finally {
      setCyclesLoading(false);
    }
  };

  const loadSectionDaily = async (sectionId: number, reportDate: string) => {
    const requestId = ++sectionDailyRequestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const [dailyResponse, missingQueueResponse] = await Promise.all([
        goalsApi.teacher.sectionDaily(sectionId, { date: reportDate }),
        goalsApi.teacher.sectionMissingQueue(sectionId, { date: reportDate, limit: 100 }),
      ]);
      if (requestId !== sectionDailyRequestIdRef.current) return;
      const nextRows = Array.isArray(dailyResponse?.rows) ? dailyResponse.rows : [];
      setRows(nextRows);
      setSchoolCalendar(dailyResponse?.school_calendar || null);

      setMissingQueueItems(Array.isArray(missingQueueResponse?.items) ? missingQueueResponse.items : []);
      setMissingQueueTotal(Number(missingQueueResponse?.total_items || 0));
      setLoadedRosterContext({ sectionId, date: reportDate });
      setSelectedStudentId((current) => {
        if (current && nextRows.some((row: SectionDailyRow) => row.student_id === current)) {
          return current;
        }
        return null;
      });
    } catch (err: any) {
      if (requestId !== sectionDailyRequestIdRef.current) return;
      setError(err?.response?.data?.error || 'Failed to load goals dashboard.');
      setRows([]);
      setSchoolCalendar(null);

      setMissingQueueItems([]);
      setMissingQueueTotal(0);
      setLoadedRosterContext(null);
      setSelectedStudentId(null);
    } finally {
      if (requestId === sectionDailyRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const loadStudentDetail = async (studentId: number, sectionId: number, reportDate: string) => {
    const requestId = ++studentDetailRequestIdRef.current;
    try {
      setError('');

      const response = await goalsApi.teacher.studentDaily(studentId, { section_id: sectionId, date: reportDate });
      if (requestId !== studentDetailRequestIdRef.current) return;
      const summaryFromApi = response?.external_work_summary || {};
      const currentPrimaryGoal = (response?.active_primary_goal || null) as StudentPrimaryGoal | null;
      const activePrimaryGoal = (currentPrimaryGoal || response?.review_primary_goal || null) as StudentPrimaryGoal | null;
      const loadedCycle = (response?.cycle || null) as DailyGoalCycle | null;
      if (!pendingImportedPrimaryDraftRef.current) {
        setPrimarySupportDraft(primaryGoalSupportDraft(currentPrimaryGoal));
      }
      if (!pendingImportedDailyDraftRef.current) {
        setDailySupportDraft(dailyPlanSupportDraft(loadedCycle));
      }
      setGoalSupportError('');
      if (!pendingImportedPrimaryDraftRef.current && !pendingImportedDailyDraftRef.current) {
        setGoalPlanImportSource('');
        setGoalPlanImportMessage('');
        setGoalPlanImportWarnings([]);
      }
      setStudentDetail({
        cycle: loadedCycle,
        comments: Array.isArray(response?.comments) ? response.comments : [],
        history: Array.isArray(response?.history) ? response.history : [],
        externalWorkSummary: {
          total_blocks: Number(summaryFromApi?.total_blocks || 0),
          active_blocks: Number(summaryFromApi?.active_blocks || 0),
          completed_blocks: Number(summaryFromApi?.completed_blocks || 0),
          switch_count: Number(summaryFromApi?.switch_count || 0),
          last_event_type: String(summaryFromApi?.last_event_type || ''),
          last_event_at: summaryFromApi?.last_event_at ? String(summaryFromApi?.last_event_at) : null,
        },
        externalWorkEvents: Array.isArray(response?.external_work_events) ? response.external_work_events : [],
        aiAssistEvents: Array.isArray(response?.ai_assist_events) ? response.ai_assist_events : [],
        goalQuality: response?.goal_quality || null,
        triage: response?.triage || null,
        followupTriage: response?.followup_triage || null,
        projectWork: response?.project_work || null,
        externalRisks: response?.external_risks || null,
        currentPrimaryGoal,
        activeGoals: response?.active_primary_goals || (currentPrimaryGoal ? [currentPrimaryGoal] : []),
        activePrimaryGoal,
        currentWeeklyCheckpoint: response?.current_weekly_checkpoint || null,
        weeklyCheckpoints: Array.isArray(response?.weekly_checkpoints) ? response.weekly_checkpoints : [],
        weekStartDate: String(response?.week_start_date || ''),
      });

    } catch (err: any) {
      if (requestId !== studentDetailRequestIdRef.current) return;
      setError(err?.response?.data?.error || 'Failed to load student goal detail.');
      setStudentDetail(null);

    }
  };

  const updateTeacherMilestone = async (
    row: SectionDailyRow,
    milestone: GoalMilestone,
    payload: Parameters<typeof goalsApi.teacherMilestones.update>[4],
  ) => {
    if (!selectedSectionId || !row.primary_goal) return;
    try {
      setError('');
      await goalsApi.teacherMilestones.update(
        selectedSectionId,
        row.student_id,
        row.primary_goal.id,
        milestone.id,
        payload,
      );
      await loadSectionDaily(selectedSectionId, selectedDate);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to steer milestone.');
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (!allSections.length) {
      setSelectedSectionId(null);
      return;
    }
    setSelectedSectionId((current) => (
      current && allSections.some((section) => section.id === current)
        ? current
        : preferredTeacherSection(activeSections.length ? activeSections : allSections)
    ));
  }, [activeSections, allSections]);

  useEffect(() => {
    if (!selectedSectionId) {
      sectionDailyRequestIdRef.current += 1;
      studentDetailRequestIdRef.current += 1;
      setLoadedRosterContext(null);
      setSelectedStudentId(null);
      setStudentDetail(null);
      setRows([]);
      setSchoolCalendar(null);

      setMissingQueueItems([]);
      setMissingQueueTotal(0);
      setError('');
      setLoading(false);
      return;
    }
    sectionDailyRequestIdRef.current += 1;
    studentDetailRequestIdRef.current += 1;
    setLoadedRosterContext(null);
    const requested = new URLSearchParams(window.location.search);
    setSelectedStudentId(Number(requested.get('section')) === selectedSectionId ? Number(requested.get('student')) || null : null);
    setStudentDetail(null);

    setRows([]);
    setSchoolCalendar(null);

    setMissingQueueItems([]);
    setMissingQueueTotal(0);
    setLoading(false);
    if (selectedSectionHasGoalsActive) {
      loadSectionDaily(selectedSectionId, selectedDate);
    }
  }, [selectedSectionHasGoalsActive, selectedSectionId, selectedDate]);

  useEffect(() => {
    setSectionGoalsEnabled(Boolean(selectedSection?.goals_feature_enabled));
    const nextMode = String(selectedSection?.goal_mode || (selectedSection?.goals_feature_enabled ? 'full' : 'off')) as GoalMode;
    setSectionGoalMode(['off', 'basic', 'guided', 'full'].includes(nextMode) ? nextMode : 'off');
    const grades = Array.isArray(selectedSection?.goals_enabled_grades) ? (selectedSection?.goals_enabled_grades || []) : [];
    setSectionGoalGrades(Array.isArray(grades) ? grades : []);
    const preset = String(selectedSection?.goal_policy?.preset_key || 'legacy') as GoalPolicyPreset;
    setSectionGoalPreset(['grades_6_8', 'grades_9_10', 'grades_11_12'].includes(preset) ? preset : 'legacy');
  }, [selectedSection?.id, selectedSection?.goals_feature_enabled, selectedSection?.goal_mode, selectedSection?.goals_enabled_grades, selectedSection?.goal_policy]);

  useEffect(() => {
    setInstructionalCycles([]);
    setEditingCycleId(null);
    setCycleError('');
    const defaultAudience = String(selectedSection?.goal_policy?.preset_key || '') === 'grades_11_12'
      ? ['11th', '12th']
      : (Array.isArray(selectedSection?.goals_enabled_grades) ? (selectedSection?.goals_enabled_grades || []) : []);
    setCycleDraft(emptyCycleDraft(defaultAudience));
    if (selectedSectionId) {
      void loadInstructionalCycles(selectedSectionId);
    }
  }, [selectedSectionId]);

  useEffect(() => {
    if (!selectedStudentId || !selectedSectionId) {
      studentDetailRequestIdRef.current += 1;
      setStudentDetail(null);
      return;
    }
    if (
      !loadedRosterContext
      || loadedRosterContext.sectionId !== selectedSectionId
      || loadedRosterContext.date !== selectedDate
      || !rows.some((row) => row.student_id === selectedStudentId)
    ) {
      setStudentDetail(null);
      return;
    }
    loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate);
  }, [loadedRosterContext, rows, selectedStudentId, selectedSectionId, selectedDate]);

  const handleSupportAction = async (row: SectionDailyRow, action: 'acknowledge' | 'resolve') => {
    if (!selectedSectionId || !row.support_request?.block_id) return;
    const loadingKey = `${row.student_id}:${action}`;
    try {
      setError('');
      setSupportActionLoading(loadingKey);
      await goalsApi.teacher.externalWorkSupport(selectedSectionId, row.student_id, {
        block_id: row.support_request.block_id,
        action,
      });
      await loadSectionDaily(selectedSectionId, selectedDate);
      if (selectedStudentId === row.student_id) {
        await loadStudentDetail(row.student_id, selectedSectionId, selectedDate);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || `Failed to ${action} the help request.`);
    } finally {
      setSupportActionLoading('');
    }
  };

  const handleBypass = async (studentId: number) => {
    if (!selectedSectionId) return;
    const reason = String(bypassReasonByStudent[studentId] || '').trim();
    if (!reason) {
      setError('Bypass reason is required.');
      return;
    }
    try {
      setError('');
      await goalsApi.teacher.bypass(studentId, {
        section_id: selectedSectionId,
        reason,
      });
      setBypassReasonByStudent((prev) => ({ ...prev, [studentId]: '' }));
      await loadSectionDaily(selectedSectionId, selectedDate);
      if (selectedStudentId === studentId) {
        await loadStudentDetail(studentId, selectedSectionId, selectedDate);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to bypass daily gate.');
    }
  };

  const handleGoalReview = async (goal: StudentPrimaryGoal, decision: 'approved' | 'needs_revision') => {
    if (!selectedSectionId || !selectedStudentId) return;
    const note = String(goalReviewNotes[goal.id] || '').trim();
    if (decision === 'needs_revision' && !note) {
      setGoalReviewMessage('Describe the specific change you want the student to make.');
      return;
    }
    try {
      setGoalReviewSaving(true);
      setGoalReviewMessage('');
      await goalsApi.teacher.reviewGoal(selectedSectionId, selectedStudentId, goal.id, {
        decision, note, definition_revision: goal.definition_revision, definition_digest: goal.definition_digest,
      });
      await loadSectionDaily(selectedSectionId, selectedDate);
      await loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate);
      setGoalReviewNotes((current) => ({ ...current, [goal.id]: '' }));
      setGoalReviewMessage(decision === 'approved' ? 'Goal approved. The student can see your feedback.' : 'Revision requested. The student can continue daily work.');
    } catch (err: any) {
      setGoalReviewMessage(err?.response?.data?.error === 'stale_goal_revision'
        ? 'This goal changed since you opened it. Reopen the student’s goal and review the latest version.'
        : 'Could not save the teacher review. Please try again.');
    } finally {
      setGoalReviewSaving(false);
    }
  };

  const loadGoalPlanImportIntoDrafts = (source = goalPlanImportSource) => {
    try {
      const imported = parseGoalPlanImport(source);
      const includePrimaryGoal = goalPlanImportScope !== 'daily_plan_only';
      const includeDailyPlan = goalPlanImportScope !== 'primary_goal_only';
      const primaryFieldCount = Object.keys(imported.primaryGoal).length;
      const dailyFieldCount = Object.keys(imported.dailyPlan).length;
      if (includePrimaryGoal && primaryFieldCount === 0) {
        throw new Error('No primary or five-week goal fields were found for the selected import scope.');
      }
      if (includeDailyPlan && dailyFieldCount === 0) {
        throw new Error('No daily-plan fields were found for the selected import scope.');
      }
      if (includePrimaryGoal) {
        pendingImportedPrimaryDraftRef.current = true;
        setPrimarySupportDraft((current) => ({
          ...current,
          ...imported.primaryGoal,
          ...(imported.support.support_mode ? { support_mode: imported.support.support_mode } : {}),
          ...(imported.support.student_visible_rationale
            ? { student_visible_rationale: imported.support.student_visible_rationale }
            : {}),
        }));
      }
      if (includeDailyPlan) {
        pendingImportedDailyDraftRef.current = true;
        setDailySupportDraft((current) => ({
          ...current,
          ...imported.dailyPlan,
          ...(imported.support.support_mode ? { support_mode: imported.support.support_mode } : {}),
          ...(imported.support.student_visible_rationale
            ? { student_visible_rationale: imported.support.student_visible_rationale }
            : {}),
        }));
      }
      const selectedFieldCount = (
        (includePrimaryGoal ? primaryFieldCount : 0)
        + (includeDailyPlan ? dailyFieldCount : 0)
        + Object.keys(imported.support).length
      );
      const scopeWarnings = [...imported.warnings];
      if (!includePrimaryGoal && primaryFieldCount > 0) {
        scopeWarnings.push('Primary/five-week goal fields were skipped so the current goal stays unchanged.');
      }
      if (!includeDailyPlan && dailyFieldCount > 0) {
        scopeWarnings.push("Daily-plan fields were skipped because this import is for the primary/five-week goal only.");
      }
      const scopeLabel = goalPlanImportScope === 'daily_plan_only'
        ? "today's plan only"
        : goalPlanImportScope === 'primary_goal_only'
          ? 'primary/five-week goal only'
          : "primary/five-week goal and today's plan";
      setGoalPlanImportMessage(`${selectedFieldCount} instructional field${selectedFieldCount === 1 ? '' : 's'} loaded for ${scopeLabel}. Nothing has been saved yet.`);
      setGoalPlanImportWarnings(Array.from(new Set(scopeWarnings)));
      setGoalPlanImportSource('');
      setGoalSupportError('');
      setGoalSupportSaved('');
    } catch (err: any) {
      setGoalPlanImportMessage('');
      setGoalPlanImportWarnings([]);
      setGoalSupportError(err?.message || 'The import could not be read.');
    }
  };

  const loadGoalPlanImportFile = async (file?: File | null) => {
    if (!file) return;
    const detailRequest = studentDetailRequestIdRef.current;
    if (file.size > 64 * 1024) {
      setGoalSupportError('Import files must be 64 KB or smaller.');
      return;
    }
    try {
      const source = await file.text();
      if (detailRequest !== studentDetailRequestIdRef.current) return;
      setGoalPlanImportSource(source);
      setGoalPlanImportMessage(`${file.name} loaded locally. Review the source, then load its instructional fields.`);
      setGoalPlanImportWarnings([]);
      setGoalSupportError('');
    } catch {
      setGoalSupportError('The selected import file could not be read.');
    }
  };

  const savePrimaryGoalSupport = async () => {
    if (!selectedSectionId || !selectedStudentId) return;
    if (!confirmGoalChange) {
      setGoalSupportError('Confirm that you intend to update this student’s five-week goal.');
      return;
    }
    const rationale = primarySupportDraft.student_visible_rationale.trim();
    if (!rationale) {
      setGoalSupportError('Add a brief student-visible reason for this support.');
      return;
    }
    if (!primarySupportDraft.goal_purpose || !primarySupportDraft.title.trim() || !primarySupportDraft.why_text.trim() || !primarySupportDraft.target_metric_text.trim()) {
      setGoalSupportError('Choose a goal purpose and complete the goal, why, and progress target.');
      return;
    }
    try {
      setGoalSupportSaving('primary');
      setGoalSupportError('');
      setGoalSupportSaved('');
      await goalsApi.teacher.savePrimaryGoalSupport(selectedSectionId, selectedStudentId, {
        goal_id: studentDetail?.currentPrimaryGoal?.id,
        support_mode: primarySupportDraft.support_mode,
        student_visible_rationale: rationale,
        goal_track: studentDetail?.currentPrimaryGoal?.goal_track || 'required_pathway',
        goal_purpose: primarySupportDraft.goal_purpose,
        title: primarySupportDraft.title.trim(),
        why_text: primarySupportDraft.why_text.trim(),
        target_metric_text: primarySupportDraft.target_metric_text.trim(),
      });
      pendingImportedPrimaryDraftRef.current = false;
      await loadSectionDaily(selectedSectionId, selectedDate);
      await loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate);
      setGoalSupportSaved('Goal support saved. Review and approve the updated goal in Teacher goal review. The student can continue daily work.');
    } catch (err: any) {
      setGoalSupportError(apiErrorMessage(err, 'Failed to save goal support.'));
    } finally {
      setGoalSupportSaving('');
    }
  };

  const saveDailyPlanSupport = async () => {
    if (!selectedSectionId || !selectedStudentId) return;
    const rationale = dailySupportDraft.student_visible_rationale.trim();
    if (!rationale) {
      setGoalSupportError('Add a brief student-visible reason for this support.');
      return;
    }
    if (dailySupportDraft.support_mode === 'teacher_stated' && (
      !dailySupportDraft.plan_target.trim()
      || !dailySupportDraft.plan_first_step.trim()
      || !dailySupportDraft.plan_success_criteria.trim()
    )) {
      setGoalSupportError('A teacher-stated plan needs a task, first step, and success check.');
      return;
    }
    try {
      setGoalSupportSaving('daily');
      setGoalSupportError('');
      setGoalSupportSaved('');
      await goalsApi.teacher.saveDailyPlanSupport(selectedSectionId, selectedStudentId, {
        date: selectedDate,
        support_mode: dailySupportDraft.support_mode,
        student_visible_rationale: rationale,
        plan_track: dailySupportDraft.plan_track,
        plan_target: dailySupportDraft.plan_target.trim(),
        plan_first_step: dailySupportDraft.plan_first_step.trim(),
        plan_success_criteria: dailySupportDraft.plan_success_criteria.trim(),
        protected_goal_minutes: dailySupportDraft.protected_goal_minutes ? Number(dailySupportDraft.protected_goal_minutes) : null,
        planned_secondary_work: dailySupportDraft.planned_secondary_work.trim(),
        planned_work_mode: dailySupportDraft.planned_work_mode,
      });
      pendingImportedDailyDraftRef.current = false;
      await loadSectionDaily(selectedSectionId, selectedDate);
      await loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate);
      setGoalSupportSaved('Daily plan support saved. The student will see the plan and reason.');
    } catch (err: any) {
      setGoalSupportError(apiErrorMessage(err, 'Failed to save daily plan support.'));
    } finally {
      setGoalSupportSaving('');
    }
  };

  const handleComment = async () => {
    const cycleId = studentDetail?.cycle?.id;
    if (!cycleId) {
      setError('No active cycle available for comment.');
      return;
    }
    const text = commentText.trim();
    if (!text) {
      setError('Comment text is required.');
      return;
    }
    try {
      setError('');
      await goalsApi.teacher.comment({ cycle_id: cycleId, text });
      setCommentText('');
      if (selectedStudentId && selectedSectionId) {
        await loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to post comment.');
    }
  };

  const handleSavePolicy = async () => {
    if (!selectedSectionId) return;
    try {
      setSavingPolicy(true);
      setError('');
      const policyPayload = sectionGoalPreset === 'legacy'
        ? {
            goals_feature_enabled: sectionGoalsEnabled && sectionGoalMode !== 'off',
            goal_mode: sectionGoalsEnabled ? sectionGoalMode : 'off',
            goals_enabled_grades: sectionGoalGrades,
          }
        : { goal_policy_preset: sectionGoalPreset };
      const response = await sectionsApi.update(selectedSectionId, policyPayload);
      const updated = response?.data;
      const updatedSectionIsActive = (
        Boolean(updated?.goals_feature_enabled) && String(updated?.goal_mode || 'off') !== 'off'
      );
      setAllSections((prev) => prev.map((section) => (
        section.id === selectedSectionId
          ? {
              ...section,
              goals_feature_enabled: Boolean(updated?.goals_feature_enabled),
              goal_mode: String(updated?.goal_mode || 'off') as GoalMode,
              goals_enabled_grades: Array.isArray(updated?.goals_enabled_grades) ? updated.goals_enabled_grades : [],
              goal_policy: updated?.goal_policy,
            }
              : section
      )));
      if (!updatedSectionIsActive) return;
      await loadSectionDaily(selectedSectionId, selectedDate);
      if (selectedStudentId) {
        await loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save section goals policy.');
    } finally {
      setSavingPolicy(false);
    }
  };

  const resetCycleDraft = () => {
    const defaultAudience = sectionGoalPreset === 'grades_11_12'
      ? ['11th', '12th']
      : sectionGoalGrades;
    setEditingCycleId(null);
    setCycleDraft(emptyCycleDraft(defaultAudience));
    setCycleError('');
  };

  const handleEditCycle = (cycle: InstructionalCycle) => {
    setEditingCycleId(cycle.id);
    setCycleDraft({
      title: cycle.title,
      cycle_kind: cycle.cycle_kind,
      audience_grades: Array.isArray(cycle.audience_grades) ? cycle.audience_grades : [],
      start_date: cycle.start_date,
      end_date: cycle.end_date,
      sequence: String(cycle.sequence || 1),
    });
    setCycleError('');
  };

  const handleSaveCycle = async () => {
    if (!selectedSectionId) return;
    if (!cycleDraft.title.trim() || !cycleDraft.start_date || !cycleDraft.end_date) {
      setCycleError('Add a cycle name, start date, and end date.');
      return;
    }
    if (cycleDraft.end_date < cycleDraft.start_date) {
      setCycleError('The cycle end date must be on or after its start date.');
      return;
    }
    const payload = {
      title: cycleDraft.title.trim(),
      cycle_kind: cycleDraft.cycle_kind,
      audience_grades: cycleDraft.audience_grades,
      start_date: cycleDraft.start_date,
      end_date: cycleDraft.end_date,
      sequence: Math.max(1, Number(cycleDraft.sequence) || 1),
    };
    try {
      setCycleSaving(true);
      setCycleError('');
      if (editingCycleId) {
        await goalsApi.instructionalCycles.update(selectedSectionId, editingCycleId, payload);
      } else {
        await goalsApi.instructionalCycles.create(selectedSectionId, payload);
      }
      resetCycleDraft();
      await loadInstructionalCycles(selectedSectionId);
    } catch (err: any) {
      setCycleError(apiErrorMessage(err, 'Failed to save the instructional cycle.'));
    } finally {
      setCycleSaving(false);
    }
  };

  const handleCycleTransition = async (cycle: InstructionalCycle, action: CycleTransition) => {
    if (!selectedSectionId) return;
    if (action === 'review' && !window.confirm(
      `Begin review for ${cycle.title}? This closes active primary goals linked to this cycle.`,
    )) return;
    if (action === 'finalize' && !window.confirm(
      `Finalize ${cycle.title}? The cycle and its evidence become read-only.`,
    )) return;
    try {
      setCycleSaving(true);
      setCycleError('');
      await goalsApi.instructionalCycles.transition(selectedSectionId, cycle.id, action);
      await loadInstructionalCycles(selectedSectionId);
      if (selectedSectionHasGoalsActive) {
        await loadSectionDaily(selectedSectionId, selectedDate);
      }
    } catch (err: any) {
      setCycleError(apiErrorMessage(err, `Failed to ${action} the instructional cycle.`));
    } finally {
      setCycleSaving(false);
    }
  };

  const handleGoalCycleConnection = async (row: SectionDailyRow, cycle: InstructionalCycle, connect: boolean) => {
    if (!selectedSectionId || !row.primary_goal) return;
    if (!connect && !window.confirm(
      `Disconnect ${row.student_name}'s goal from ${cycle.title}? Cycle-specific review and evidence tools will no longer use this goal.`,
    )) return;
    try {
      setGoalCycleSavingId(row.primary_goal.id);
      setCycleError('');
      await goalsApi.instructionalCycles.setGoalCycle(
        selectedSectionId,
        row.student_id,
        row.primary_goal.id,
        connect ? cycle.id : null,
      );
      await loadSectionDaily(selectedSectionId, selectedDate);
      if (selectedStudentId === row.student_id) {
        await loadStudentDetail(row.student_id, selectedSectionId, selectedDate);
      }
    } catch (err: any) {
      setCycleError(apiErrorMessage(err, `Failed to ${connect ? 'connect' : 'disconnect'} the primary goal.`));
    } finally {
      setGoalCycleSavingId(null);
    }
  };

  const statusChipClass = (statusValue: SectionDailyRow['status']) => {
    if (statusValue === 'checked_out') return 'bg-emerald-100 text-emerald-700';
    if (statusValue === 'checked_in') return 'bg-amber-100 text-amber-700';
    if (statusValue === 'not_applicable') return 'bg-slate-100 text-slate-600';
    return 'bg-rose-100 text-rose-700';
  };

  const priorityChipClass = (level: TeacherPriorityLevel) => {
    if (level === 'needs_attention') return 'bg-rose-100 text-rose-700';
    if (level === 'monitor') return 'bg-amber-100 text-amber-700';
    if (level === 'not_applicable') return 'bg-slate-100 text-slate-600';
    return 'bg-emerald-100 text-emerald-700';
  };

  const priorityBorderClass = (level: TeacherPriorityLevel) => {
    if (level === 'needs_attention') return 'border-l-rose-400';
    if (level === 'monitor') return 'border-l-amber-400';
    if (level === 'not_applicable') return 'border-l-slate-300';
    return 'border-l-emerald-400';
  };

  const goalQualityChipClass = (level?: string) => {
    if (level === 'strong') return 'bg-emerald-100 text-emerald-700';
    if (level === 'needs_clarification') return 'bg-amber-100 text-amber-700';
    if (level === 'weak') return 'bg-orange-100 text-orange-700';
    if (level === 'missing') return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-600';
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const formatFocusTime = (seconds?: number | null) => {
    if (seconds === null || seconds === undefined) return '';
    const wholeMinutes = Math.floor(Math.max(0, seconds) / 60);
    const remainingSeconds = Math.max(0, seconds) % 60;
    return `${wholeMinutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const focusStateClass = (state?: NonNullable<SectionDailyRow['focus_state']>['state']) => {
    if (state === 'active') return 'bg-indigo-100 text-indigo-800';
    if (state === 'paused' || state === 'reset_due') return 'bg-amber-100 text-amber-800';
    if (state === 'finished') return 'bg-emerald-100 text-emerald-800';
    return 'bg-slate-100 text-slate-700';
  };

  const missingQueueDueLabel = (item: SectionMissingQueueItem) => {
    const rawValue = item.due_date || item.due_at || '';
    if (!rawValue) return 'No due date';
    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) return rawValue;
    return parsed.toLocaleDateString();
  };

  const selectedDetailReason = selectedStudentRow
    ? priorityReasonForRow(selectedStudentRow)
    : priorityReason(studentDetail?.followupTriage || studentDetail?.triage);

  const selectStudentForDetail = (studentId: number, trigger: HTMLButtonElement) => {
    if (goalSupportSaving || goalReviewSaving) return;
    if (selectedStudentId && (pendingImportedPrimaryDraftRef.current || pendingImportedDailyDraftRef.current)
      && !window.confirm('Discard the unsaved imported fields and open another student?')) return;
    setGoalReviewMessage('');
    if (!studentDetailRef.current?.contains(trigger)) studentDetailTriggerRef.current = trigger;
    pendingImportedPrimaryDraftRef.current = false;
    pendingImportedDailyDraftRef.current = false;
    setGoalPlanImportScope('daily_plan_only');
    setDetailTab(workspaceView === 'goals' ? 'goal' : 'today');
    setConfirmGoalChange(false);
    setGoalSupportError('');
    setGoalSupportSaved('');
    setGoalPlanImportSource('');
    setGoalPlanImportMessage('');
    setGoalPlanImportWarnings([]);
    if (selectedStudentId !== studentId) {
      studentDetailRequestIdRef.current += 1;
      setStudentDetail(null);
      setCommentText('');
    }
    setSelectedStudentId(studentId);
    setDetailNavigationStudentId(studentId);
  };

  const closeStudentDetail = () => {
    if (goalSupportSaving || goalReviewSaving) return;
    if ((pendingImportedPrimaryDraftRef.current || pendingImportedDailyDraftRef.current)
      && !window.confirm('Discard the unsaved imported fields and return to the roster?')) return;
    const trigger = studentDetailTriggerRef.current;
    setDetailNavigationStudentId(null);
    setSelectedStudentId(null);
    if (trigger?.isConnected) {
      window.requestAnimationFrame(() => trigger.focus());
    }
  };

  useEffect(() => {
    if (!detailNavigationStudentId || selectedStudentId !== detailNavigationStudentId) return;
    const detailPanel = studentDetailRef.current;
    if (!detailPanel) return;
    detailPanel.focus({ preventScroll: true });
    detailPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setDetailNavigationStudentId(null);
  }, [detailNavigationStudentId, selectedStudentId]);

  const dailyComments = studentDetail?.cycle
    ? studentDetail.comments.filter((comment) => comment.cycle === studentDetail.cycle?.id)
    : [];

  return (
    <OptionalAppShell enabled={redesignEnabled} role="teacher" navItems={teacherV2NavItems('goals')} title="Goals">
      {!redesignEnabled && <TeacherNav activeTab="goals" />}
      <div className={redesignEnabled ? 'max-w-7xl mx-auto flex flex-col gap-3' : 'max-w-7xl mx-auto flex flex-col gap-3 p-6'}>
        <style>{`
          @media print {
            @page { size: landscape; margin: 0.45in; }
            body * { visibility: hidden !important; }
            #daily-goal-summary-report, #daily-goal-summary-report * { visibility: visible !important; }
            #daily-goal-summary-report { position: absolute; inset: 0 auto auto 0; width: 100%; border: 0 !important; box-shadow: none !important; }
            #daily-goal-summary-report .daily-goal-report-actions { display: none !important; }
            #daily-goal-summary-report table { font-size: 9pt; }
            #daily-goal-summary-report th, #daily-goal-summary-report td { padding: 5px !important; }
            #daily-goal-summary-report tr { break-inside: avoid; }
          }
        `}</style>
        <h1 className="text-2xl font-semibold text-gray-900">Daily plans &amp; goals</h1>
        <p className="text-sm text-gray-500 mt-1">Review daily plans, support achievable goals, and follow up on student work.</p>
        {!activeSections.length && allSections.length > 0 && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            No sections currently have goals enabled. Select a section below to turn goals on.
          </div>
        )}

        {error && (
          <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div hidden={Boolean(selectedStudentId)}>
        <nav className="flex flex-wrap gap-2" aria-label="Goal workspace views">
          {([['daily','Daily plans'],['goals','Goal approvals'],['report','Daily report'],['settings','Cycles & settings']] as const).map(([value,label]) => <button type="button" key={value} aria-pressed={workspaceView === value} className={'rounded border px-3 py-2 text-sm ' + (workspaceView === value ? 'bg-indigo-700 text-white' : 'bg-white')} onClick={() => setWorkspaceView(value)}>{label}</button>)}
        </nav>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <PageClassSelector><div>
            <label className="block text-xs text-gray-500">Section</label>
            <select
              className="mt-1 w-full border rounded px-3 py-2"
              value={selectedSectionId || ''}
              onChange={(event) => setSelectedSectionId(Number(event.target.value))}
            >
              {!allSections.length && <option value="">No sections found</option>}
              {allSections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}{isGoalsSectionActive(section) ? '' : ' (goals off)'}
                </option>
              ))}
            </select>
          </div></PageClassSelector>
          <div>
            <label className="block text-xs text-gray-500">Date</label>
            <input
              type="date"
              className="mt-1 w-full border rounded px-3 py-2"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
            />
          </div>
        </div>

        {schoolCalendar?.is_school_wide && !schoolCalendar.is_school_day && (
          <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-indigo-950" role="status">
            <p className="font-semibold">Elevate school-wide closure</p>
            <p className="mt-1 text-sm">
              {schoolCalendar.closure_label || 'No school'} — daily goals and check-ins are not scheduled for any section.
            </p>
          </div>
        )}

        <section hidden={workspaceView === 'settings'} id="daily-goal-summary-report" className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm" aria-labelledby="daily-goal-summary-heading">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">Daily report</p>
              <h2 id="daily-goal-summary-heading" className="mt-1 text-lg font-semibold text-slate-950">{workspaceView === 'report' ? 'Daily Student Goal Summary' : workspaceView === 'goals' ? 'Goal approvals' : 'Today at a glance'}</h2>
              <p className="mt-1 text-sm text-slate-600">{selectedSection?.name || 'Select a section'} · {selectedDate}</p>
            </div>
            <button
              type="button"
              hidden={workspaceView !== 'report'} className="daily-goal-report-actions rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              onClick={() => window.print()}
              disabled={!selectedSectionHasGoalsActive || loading || rows.length === 0}
            >
              Print / Save PDF
            </button>
          </div>
          <div className="grid grid-cols-2 gap-px border-b border-slate-200 bg-slate-200 sm:grid-cols-4" aria-label="Daily report totals">
            {[
              ['Students', dailyReportCounts.applicable],
              ['Checked in', dailyReportCounts.checkedIn],
              ['Checked out', dailyReportCounts.checkedOut],
              ['Next step missing', dailyReportCounts.missingNextStep],
            ].map(([label, value]) => (
              <div key={String(label)} className="bg-slate-50 px-3 py-2">
                <span className="text-xs text-slate-500">{label}</span>
                <span className="ml-2 font-semibold text-slate-900">{value}</span>
              </div>
            ))}
          </div>
          <div className="daily-goal-report-actions flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2" aria-label="Roster filters">
            {([
              ['all', 'All students'],
              ['followup', 'Needs follow-up'],
              ['missing_priority', 'Missing priority'],
              ['reviews_due', 'Reviews due'],
              ['wrapups', 'Wrap-ups'],
              ['help', 'Help requested'],
            ] as Array<[RosterFilter, string]>).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={rosterFilter === value}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${rosterFilter === value ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                onClick={() => setRosterFilter(value)}
              >
                {label} ({rosterFilterCounts[value]})
              </button>
            ))}
            <label className="ml-1 flex items-center gap-2 text-xs font-medium text-slate-600">
              Planned mode
              <select className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800" value={workModeFilter} onChange={(event) => setWorkModeFilter(event.target.value)}>
                <option value="all">All modes</option><option value="five_week_goal">Five-week goal</option><option value="nerd_herd">Nerd Herd</option><option value="live_lesson">Live lesson</option><option value="personal_project">Personal project</option><option value="required_recovery">Required / recovery</option><option value="exploration">Exploration</option><option value="transition_cleanup">Transition / cleanup</option>
              </select>
            </label>
            <span className="ml-auto text-xs font-medium text-slate-600" role="status">Showing {filteredRosterRows.length} of {rows.length}</span>
          </div>
          {loading ? (
            <div className="p-4 text-sm text-slate-500">Loading daily report...</div>
          ) : !selectedSectionHasGoalsActive ? (
            <div className="p-4 text-sm text-slate-500">Goals are off for this section, so no daily report is available.</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No students found for the selected section and date.</div>
          ) : filteredRosterRows.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No students match this filter. Choose All students to return to the full roster.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse text-left text-xs text-slate-700">
                <thead className="bg-slate-100 text-slate-900">
                  <tr>
                    <th scope="col" className="w-[15%] border-b border-r border-slate-200 px-2 py-2 font-semibold">Student</th>
                    <th scope="col" hidden={workspaceView === 'daily'} className="border-b px-2 py-2 font-semibold">Current-cycle goal</th>
                    <th scope="col" className="w-[23%] border-b border-r border-slate-200 px-2 py-2 font-semibold">Today&apos;s priority</th>
                    <th scope="col" className="w-[13%] border-b border-r border-slate-200 px-2 py-2 font-semibold">Daily status</th>
                    <th scope="col" className="hidden w-[22%] border-b border-r border-slate-200 px-2 py-2 font-semibold sm:table-cell">Follow-up</th>
                    <th scope="col" className="w-[10%] border-b border-slate-200 px-2 py-2 font-semibold">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRosterRows.map((row) => (
                    <tr key={`daily-report-${row.student_id}`} className="align-top even:bg-slate-50">
                      <td className="border-b border-r border-slate-200 px-2 py-2">
                        <div className="font-semibold text-slate-950">{row.student_name}</div>
                        {row.grade_level && <div className="mt-0.5 text-[11px] text-slate-500">Grade {row.grade_level}</div>}
                      </td>
                      <td hidden={workspaceView === 'daily'} className="border-b px-2 py-2"><p className="line-clamp-2" title={row.primary_goal?.title || ''}>{row.primary_goal?.title || 'No active term goal'}</p></td>
                      <td className="border-b border-r border-slate-200 px-2 py-2">
                        <p className="line-clamp-2" title={row.plan_target || ''}>{row.plan_target || 'No priority recorded'}</p>
                        {workspaceView === 'report' && row.status === 'checked_out' && <p className="mt-1 line-clamp-2 text-[11px] text-slate-500" title={`${dailyReportCheckOut(row)} ${dailyReportNextStep(row)}`}>Result: {dailyReportCheckOut(row)} · Next: {dailyReportNextStep(row)}</p>}
                      </td>
                      <td className="border-b border-r border-slate-200 px-2 py-2">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusChipClass(row.status)}`}>{dailyStatusLabel(row)}</span>
                        {workspaceView === 'goals' && <span className="mt-1 block text-xs">{row.primary_goal?.quality_workflow?.state?.replace(/_/g, ' ') || 'Not reviewed'}</span>}
                        {row.focus_state && (
                          <span
                            className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${focusStateClass(row.focus_state.state)}`}
                            title={row.focus_state.block?.title || 'Focus-block status'}
                          >
                            {row.focus_state.label}
                            {row.focus_state.state === 'active' && row.focus_state.remaining_seconds !== null && row.focus_state.remaining_seconds !== undefined
                              ? ` · ${formatFocusTime(row.focus_state.remaining_seconds)} left`
                              : ''}
                          </span>
                        )}
                      </td>
                      <td className="hidden border-b border-r border-slate-200 px-2 py-2 sm:table-cell">
                        {workspaceView === 'report' && <p>{priorityReasonForRow(row)}</p>}
                        <p className="mt-1 font-semibold text-indigo-700">{teacherNextMoveForRow(row)}</p>
                        {row.support_request && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {row.support_request.status === 'requested' ? (
                              <button
                                type="button"
                                className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
                                disabled={Boolean(supportActionLoading)}
                                onClick={() => handleSupportAction(row, 'acknowledge')}
                              >
                                {supportActionLoading === `${row.student_id}:acknowledge` ? 'Acknowledging…' : 'Acknowledge'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
                                disabled={Boolean(supportActionLoading)}
                                onClick={() => handleSupportAction(row, 'resolve')}
                              >
                                {supportActionLoading === `${row.student_id}:resolve` ? 'Resolving…' : 'Mark resolved'}
                              </button>
                            )}
                            {row.support_request.requested_at && (
                              <span className="self-center text-[10px] text-slate-500">Since {formatDateTime(row.support_request.requested_at)}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="border-b border-slate-200 px-2 py-2">
                        <button type="button" className="rounded border border-indigo-200 px-2 py-1 font-semibold text-indigo-700 hover:bg-indigo-50" aria-label={`Review ${row.student_name}`} onClick={(event) => selectStudentForDetail(row.student_id, event.currentTarget)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="hidden" aria-labelledby="goal-attention-heading">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">Goal Attention</p>
              <h2 id="goal-attention-heading" className="mt-1 text-xl font-semibold text-slate-950">
                Which students or goals need attention today?
              </h2>
            </div>
            <span className="rounded-full bg-rose-50 px-3 py-1 text-sm font-semibold text-rose-700">
              {attentionRows.length} need teacher attention
            </span>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5" aria-label="Goal attention summary">
            {[
              ['Needs attention', triageCounts.needs_attention, 'Start here'],
              ['Missing daily updates', goalAttentionSummary.missingDailyUpdates, 'Check-in or check-out'],
              ['Off target / behind', goalAttentionSummary.offTarget, 'Progress needs review'],
              ['Check-ins / reviews', goalAttentionSummary.checkInsOrReviews, 'Due or approaching'],
              ['Definition warnings', goalAttentionSummary.qualityWarnings, 'Goal quality'],
            ].map(([label, value, note]) => (
              <div key={String(label)} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                <p className="text-xs font-medium text-slate-600">{label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{value}</p>
                <p className="mt-1 text-xs text-slate-500">{note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="hidden" aria-labelledby="goals-today-heading">
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">Today</p>
            <h2 id="goals-today-heading" className="mt-1 text-lg font-semibold text-slate-950">Students needing a teacher move</h2>
            <p className="mt-1 text-xs text-slate-500">Exceptions are ordered before students who only need monitoring.</p>
          </div>
          {loading ? (
            <div className="p-4 text-sm text-slate-500">Loading goal attention...</div>
          ) : !selectedSectionHasGoalsActive ? (
            <div className="p-4 text-sm text-slate-500">Goals are off for this section. Existing grade and section policy remains unchanged.</div>
          ) : attentionRows.length === 0 ? (
            <div className="border-l-4 border-emerald-400 bg-emerald-50 p-4">
              <p className="font-semibold text-emerald-900">Nobody needs goal attention today.</p>
              <p className="mt-1 text-sm text-emerald-800">Current goals remain available in This Week / Current Cycle below.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {attentionRows.map((row) => {
                const priorityLevel = priorityLevelForRow(row);
                return (
                  <article key={`today-${row.student_id}`} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-priority={priorityLevel}>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-950">{row.student_name}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${priorityChipClass(priorityLevel)}`}>
                          {priorityLabelForLevel(priorityLevel)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-800">
                        <span className="font-medium">Current goal:</span> {row.primary_goal?.title || 'No active primary goal recorded'}
                      </p>
                      <p className="mt-1 text-sm text-slate-700">
                        <span className="font-medium">Why today:</span> {priorityReasonForRow(row)}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Next move: {teacherNextMoveForRow(row)}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                      aria-label={`Review goal for ${row.student_name}`}
                      onClick={(event) => selectStudentForDetail(row.student_id, event.currentTarget)}
                    >
                      Review goal
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="hidden" aria-labelledby="class-wrap-up-heading">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">End of class</p>
              <h2 id="class-wrap-up-heading" className="mt-1 text-lg font-semibold text-slate-950">Class Wrap-Up</h2>
              <p className="mt-1 text-xs text-slate-500">Results, evidence, reflections, and next steps for {selectedDate}.</p>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
              {wrapUpRows.length} of {wrapUpRows.length + wrapUpPendingRows.length} wrapped up
            </span>
          </div>
          {loading ? (
            <div className="p-4 text-sm text-slate-500">Loading class wrap-up...</div>
          ) : !selectedSectionHasGoalsActive ? (
            <div className="p-4 text-sm text-slate-500">Goals are off for this section, so no class wrap-up is available.</div>
          ) : wrapUpRows.length === 0 ? (
            <div className="border-l-4 border-amber-400 bg-amber-50 p-4">
              <p className="font-semibold text-amber-900">No students have submitted a wrap-up yet.</p>
              <p className="mt-1 text-sm text-amber-800">{wrapUpPendingRows.length} student{wrapUpPendingRows.length === 1 ? '' : 's'} still need to check out.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {wrapUpRows.map((row) => (
                <article key={`wrap-up-${row.student_id}`} className="px-4 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-950">{row.student_name}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${wrapUpResultClass(row)}`}>
                          {wrapUpResultLabel(row)}
                        </span>
                        {row.checked_out_at && <span className="text-xs text-slate-500">{formatDateTime(row.checked_out_at)}</span>}
                      </div>
                      {row.plan_target && <p className="mt-2 text-sm text-slate-700"><span className="font-semibold">Today&apos;s priority:</span> {row.plan_target}</p>}
                      {row.pathway_evidence?.description && <p className="mt-1 text-sm text-slate-700"><span className="font-semibold">Evidence:</span> {row.pathway_evidence.description}</p>}
                      {row.project_evidence?.description && <p className="mt-1 text-sm text-slate-700"><span className="font-semibold">Project evidence:</span> {row.project_evidence.description}</p>}
                      {row.reflection_text && <p className="mt-1 text-sm text-slate-700"><span className="font-semibold">Reflection:</span> {row.reflection_text}</p>}
                      {row.reflection_next_step && <p className="mt-1 text-sm text-slate-700"><span className="font-semibold">Next step:</span> {row.reflection_next_step}</p>}
                      {(row.completion_rating || row.confidence_rating || (row.moved_toward_primary_goal !== null && row.moved_toward_primary_goal !== undefined)) && (
                        <p className="mt-2 text-xs text-slate-500">
                          {row.completion_rating ? `Completion ${row.completion_rating}/5` : ''}
                          {row.completion_rating && row.confidence_rating ? ' · ' : ''}
                          {row.confidence_rating ? `Confidence ${row.confidence_rating}/5` : ''}
                          {(row.completion_rating || row.confidence_rating) && row.moved_toward_primary_goal !== null && row.moved_toward_primary_goal !== undefined ? ' · ' : ''}
                          {row.moved_toward_primary_goal !== null && row.moved_toward_primary_goal !== undefined
                            ? `Moved toward main goal: ${row.moved_toward_primary_goal ? 'Yes' : 'No'}`
                            : ''}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                      aria-label={`Open wrap-up details for ${row.student_name}`}
                      onClick={(event) => selectStudentForDetail(row.student_id, event.currentTarget)}
                    >
                      Open student
                    </button>
                  </div>
                </article>
              ))}
              {wrapUpPendingRows.length > 0 && (
                <details className="bg-slate-50 px-4 py-3">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                    Still waiting on {wrapUpPendingRows.length} student{wrapUpPendingRows.length === 1 ? '' : 's'}
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {wrapUpPendingRows.map((row) => (
                      <span key={`wrap-up-pending-${row.student_id}`} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
                        {row.student_name} · {row.status === 'checked_in' ? 'checked in' : 'not checked in'}
                      </span>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </section>

        <details hidden={workspaceView !== 'settings'} open={workspaceView === 'settings'} id="section-goal-settings" className="order-last scroll-mt-24 rounded border bg-white">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-900">
            Section settings
            <span className="ml-2 text-xs font-normal text-gray-500">Open when section settings need adjustment.</span>
          </summary>
          <div className="space-y-3 border-t bg-slate-50 p-3">
            {!selectedSectionId ? (
              <div className="rounded border bg-white p-4 text-sm text-gray-500">
                Enable goals in a section to manage its goals policy here.
              </div>
            ) : (
              <>
            <div className="border rounded bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-900">Goals Policy</div>
              <div className="text-xs text-gray-500">Choose a grade-band preset explicitly, or keep custom settings.</div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={sectionGoalsEnabled}
                onChange={(event) => setSectionGoalsEnabled(event.target.checked)}
                disabled={sectionGoalPreset !== 'legacy'}
              />
              Goals enabled for this section
            </label>
          </div>
          <label className="mt-3 block text-sm text-gray-700">
            Policy preset
            <select
              className="mt-1 w-full rounded border px-3 py-2"
              value={sectionGoalPreset}
              onChange={(event) => setSectionGoalPreset(event.target.value as GoalPolicyPreset)}
            >
              {GOAL_POLICY_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label} - {preset.description}
                </option>
              ))}
            </select>
          </label>
          {sectionGoalPreset !== 'legacy' && (
            <div className="mt-2 rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
              This preset is applied only when you select Save Goals Policy.
            </div>
          )}
          {sectionGoalPreset === 'legacy' && <>
          <label className="mt-3 block text-sm text-gray-700">
            Goal mode
            <select
              className="mt-1 w-full rounded border px-3 py-2"
              value={sectionGoalMode}
              onChange={(event) => {
                const nextMode = event.target.value as GoalMode;
                setSectionGoalMode(nextMode);
                setSectionGoalsEnabled(nextMode !== 'off');
              }}
            >
              {GOAL_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </label>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-7 gap-2">
            {GRADE_OPTIONS.map((grade) => {
              const selected = sectionGoalGrades.includes(grade);
              return (
                <label key={grade} className={`px-2 py-1 border rounded text-xs flex items-center gap-2 ${selected ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-700'}`}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSectionGoalGrades((prev) => [...prev, grade]);
                      } else {
                        setSectionGoalGrades((prev) => prev.filter((item) => item !== grade));
                      }
                    }}
                  />
                  {grade}
                </label>
              );
            })}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Leave all grades unchecked to apply goals to all grades in this section.
          </div>
          </>}
          <button
            type="button"
            className="mt-3 px-3 py-1 text-sm bg-indigo-600 text-white rounded"
            onClick={handleSavePolicy}
            disabled={savingPolicy}
          >
            {savingPolicy ? 'Saving...' : 'Save Goals Policy'}
          </button>
        </div>

            <div className="rounded border bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">Instructional Cycles</div>
                  <div className="text-xs text-gray-500">
                    Cycles belong to {selectedSection?.name || 'this section'}. A performance period enables cycle-linked check-ins and five-week reviews.
                  </div>
                </div>
                {cyclesLoading && <span className="text-xs text-gray-500">Loading cycles...</span>}
              </div>

              {cycleError && (
                <div role="alert" className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {cycleError}
                </div>
              )}

              <div className="mt-3 space-y-2">
                {!cyclesLoading && instructionalCycles.length === 0 ? (
                  <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-600">
                    No cycles have been created for this section yet.
                  </div>
                ) : instructionalCycles.map((cycle) => (
                  <div key={cycle.id} className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-900">{cycle.title}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            cycle.state === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : cycle.state === 'review'
                                ? 'bg-amber-100 text-amber-800'
                                : cycle.state === 'finalized'
                                  ? 'bg-slate-200 text-slate-700'
                                  : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {cycle.state}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-600">
                          {cycleKindLabel(cycle.cycle_kind)} · {cycle.start_date} to {cycle.end_date}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Audience: {cycle.audience_grades.length > 0 ? cycle.audience_grades.join(', ') : 'all grades'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {cycle.state === 'draft' && (
                          <>
                            <button
                              type="button"
                              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50"
                              disabled={cycleSaving}
                              onClick={() => handleEditCycle(cycle)}
                            >
                              Edit draft
                            </button>
                            <button
                              type="button"
                              className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                              disabled={cycleSaving || Boolean(findBlockingActiveCycle(cycle, instructionalCycles))}
                              title={findBlockingActiveCycle(cycle, instructionalCycles)
                                ? `Begin review for ${findBlockingActiveCycle(cycle, instructionalCycles)?.title} first.`
                                : undefined}
                              onClick={() => void handleCycleTransition(cycle, 'activate')}
                            >
                              Activate
                            </button>
                          </>
                        )}
                        {cycle.state === 'active' && (
                          <button
                            type="button"
                            className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                            disabled={cycleSaving}
                            onClick={() => void handleCycleTransition(cycle, 'review')}
                          >
                            Begin Review
                          </button>
                        )}
                        {cycle.state === 'review' && (
                          <button
                            type="button"
                            className="rounded bg-slate-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                            disabled={cycleSaving}
                            onClick={() => void handleCycleTransition(cycle, 'finalize')}
                          >
                            Finalize Cycle
                          </button>
                        )}
                      </div>
                    </div>
                    {cycle.state === 'draft' && findBlockingActiveCycle(cycle, instructionalCycles) && (
                      <p className="mt-2 text-xs font-medium text-amber-700">
                        Begin review for {findBlockingActiveCycle(cycle, instructionalCycles)?.title} before activating this cycle.
                      </p>
                    )}
                    {cycle.state === 'active' && rows.some((row) => Boolean(row.primary_goal)) && (
                      <details className="mt-3 rounded border border-slate-200 bg-white">
                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-800">
                          Manage goal connections ({rows.filter((row) => row.primary_goal?.instructional_cycle === cycle.id).length} connected)
                        </summary>
                        <div className="space-y-2 border-t border-slate-200 p-3">
                          <p className="text-xs text-slate-500">
                            Connecting preserves the goal text and dates. Goals outside this cycle cannot be connected.
                          </p>
                          {rows.filter((row) => Boolean(row.primary_goal)).map((row) => {
                            const goal = row.primary_goal as StudentPrimaryGoal;
                            const connected = goal.instructional_cycle === cycle.id;
                            const compatibility = goalCycleCompatibility(row, cycle);
                            return (
                              <div key={`cycle-goal-${cycle.id}-${goal.id}`} className="flex flex-col gap-2 rounded border border-slate-200 bg-slate-50 p-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-slate-900">{row.student_name}</div>
                                  <div className="truncate text-xs text-slate-700">{goal.title}</div>
                                  <div className="mt-0.5 text-[11px] text-slate-500">
                                    {goal.start_date} to {goal.end_date}
                                    {!connected && !compatibility.eligible ? ` · ${compatibility.reason}` : ''}
                                  </div>
                                </div>
                                {connected ? (
                                  <div className="flex shrink-0 items-center gap-2">
                                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Connected</span>
                                    <button
                                      type="button"
                                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 disabled:opacity-50"
                                      disabled={goalCycleSavingId === goal.id}
                                      onClick={() => void handleGoalCycleConnection(row, cycle, false)}
                                    >
                                      Disconnect
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="shrink-0 rounded border border-indigo-300 bg-white px-3 py-1 text-xs font-medium text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={!compatibility.eligible || goalCycleSavingId === goal.id}
                                    onClick={() => void handleGoalCycleConnection(row, cycle, true)}
                                  >
                                    {goalCycleSavingId === goal.id ? 'Connecting...' : 'Connect Goal'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-slate-200 pt-4">
                <div className="text-sm font-medium text-slate-900">
                  {editingCycleId ? 'Edit cycle draft' : 'Create a cycle draft'}
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="text-sm text-slate-700">
                    Cycle name
                    <input
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      value={cycleDraft.title}
                      onChange={(event) => setCycleDraft((current) => ({ ...current, title: event.target.value }))}
                      placeholder="Cycle 1 · August–September"
                    />
                  </label>
                  <label className="text-sm text-slate-700">
                    Cycle type
                    <select
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      value={cycleDraft.cycle_kind}
                      onChange={(event) => setCycleDraft((current) => ({ ...current, cycle_kind: event.target.value as CycleKind }))}
                    >
                      <option value="performance_period">Performance period — enables performance reviews</option>
                      <option value="instructional_period">Instructional period</option>
                      <option value="transition_review">Transition / review</option>
                    </select>
                  </label>
                  <label className="text-sm text-slate-700">
                    Start date
                    <input
                      type="date"
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      value={cycleDraft.start_date}
                      onChange={(event) => setCycleDraft((current) => ({ ...current, start_date: event.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-slate-700">
                    End date
                    <input
                      type="date"
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      value={cycleDraft.end_date}
                      onChange={(event) => setCycleDraft((current) => ({ ...current, end_date: event.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-slate-700 md:max-w-40">
                    Sequence
                    <input
                      type="number"
                      min="1"
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      value={cycleDraft.sequence}
                      onChange={(event) => setCycleDraft((current) => ({ ...current, sequence: event.target.value }))}
                    />
                  </label>
                </div>

                <fieldset className="mt-3">
                  <legend className="text-sm text-slate-700">Audience grades</legend>
                  <div className="mt-1 grid grid-cols-2 gap-2 md:grid-cols-7">
                    {GRADE_OPTIONS.map((grade) => {
                      const selected = cycleDraft.audience_grades.includes(grade);
                      return (
                        <label key={`cycle-${grade}`} className={`flex items-center gap-2 rounded border px-2 py-1 text-xs ${selected ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-700'}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(event) => setCycleDraft((current) => ({
                              ...current,
                              audience_grades: event.target.checked
                                ? [...current.audience_grades, grade]
                                : current.audience_grades.filter((item) => item !== grade),
                            }))}
                          />
                          {grade}
                        </label>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Leave every grade unchecked to serve all grades in this section.</p>
                </fieldset>

                <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  Creating a cycle does not change existing goals. Goal dates must fit inside the cycle before those goals can be linked.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    disabled={cycleSaving}
                    onClick={() => void handleSaveCycle()}
                  >
                    {cycleSaving ? 'Saving...' : editingCycleId ? 'Save Draft Changes' : 'Create Draft'}
                  </button>
                  {editingCycleId && (
                    <button
                      type="button"
                      className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                      disabled={cycleSaving}
                      onClick={resetCycleDraft}
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
              </div>
            </div>

              </>
            )}
          </div>
        </details>

        <div hidden={workspaceView !== 'report' || missingQueueTotal === 0} id="priority-missing-queue" className="order-[4] scroll-mt-24 border rounded bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-900">Priority Missing Queue</div>
              <div className="text-xs text-gray-500">Open work is ordered by due date and urgency.</div>
            </div>
            <div className="text-xs text-gray-500">
              Showing {missingQueueItems.length} of {missingQueueTotal}
            </div>
          </div>
          {!selectedSectionHasGoalsActive ? (
            <div className="mt-3 text-sm text-gray-500">Goals are off for this section, so no goals queue is shown.</div>
          ) : missingQueueItems.length === 0 ? (
            <div className="mt-3 text-sm text-gray-500">No unresolved items for this date.</div>
          ) : (
            <div className="mt-3 space-y-2 max-h-64 overflow-auto">
              {missingQueueItems.map((item) => (
                <div key={`${item.student_id}-${item.kind}-${item.id}`} className="border rounded px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-gray-900">
                      #{item.priority_rank_global || '-'} {item.student_name}: {item.title}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${item.kind === 'internal_assignment' ? 'bg-slate-100 text-slate-700' : 'bg-indigo-100 text-indigo-700'}`}>
                      {item.kind === 'internal_assignment' ? 'internal' : 'external'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-2">
                    <span>{missingQueueDueLabel(item)}</span>
                    {item.overdue && <span className="text-rose-700">overdue</span>}
                    {!item.overdue && item.due_today && <span className="text-amber-700">due today</span>}
                    {item.in_grace && <span className="text-indigo-700">grace window</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="hidden">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">This Week / Current Cycle</p>
          <h2 className="mt-1 text-xl font-semibold text-slate-950">Current goals and progress</h2>
          <p className="mt-1 text-sm text-slate-600">Review the broader section after today&apos;s exceptions. Select a student to open deeper goal tools.</p>
        </div>

        <div>
          <div className="hidden">
            <div className="px-4 py-3 border-b">
              <h2 className="font-semibold text-gray-900">Current Cycle Goals</h2>
              <p className="mt-1 text-xs text-gray-500">All applicable students remain available, grouped by current goal status.</p>
            </div>
            <div className="divide-y">
              {loading ? (
                <div className="p-4 text-sm text-gray-500">Loading...</div>
              ) : !selectedSectionHasGoalsActive ? (
                <div className="p-4 text-sm text-gray-500">Goals are off for this section. Enable them below to show triage and student check-in status.</div>
              ) : rows.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No students found for selected section/date.</div>
              ) : triageGroups.map((group) => (
                <div key={group.level}>
                  <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    {group.label} ({group.rows.length})
                  </div>
                  {group.rows.map((row) => {
                    const priorityLevel = priorityLevelForRow(row);
                    return (
                      <div
                        key={row.student_id}
                        className={`border-l-4 p-4 ${priorityBorderClass(priorityLevel)} ${selectedStudentId === row.student_id ? 'bg-indigo-50' : 'bg-white'}`}
                      >
                        <button
                          type="button"
                          className="w-full text-left"
                          aria-label={`Open goal details for ${row.student_name}`}
                          onClick={(event) => selectStudentForDetail(row.student_id, event.currentTarget)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-medium text-gray-900">{row.student_name}</div>
                              {(row.current_focus?.label || row.current_focus?.title) && (
                                <div className="mt-1 text-sm font-semibold text-indigo-800">
                                  {[row.current_focus?.label, row.current_focus?.title]
                                    .filter((value, index, values) => Boolean(value) && values.indexOf(value) === index)
                                    .join(' — ')}
                                </div>
                              )}
                              {row.plan_target && (
                                <div className="mt-1 text-xs text-gray-700">
                                  <span className="font-semibold">Priority:</span> {row.plan_target}
                                </div>
                              )}
                              {row.current_focus?.secondary_title && (
                                <div className="mt-1 text-xs text-gray-700">
                                  <span className="font-semibold">Optional personal project:</span> {row.current_focus.secondary_title}
                                </div>
                              )}
                              {row.project_plan_target && (
                                <div className="mt-1 text-xs text-gray-700">
                                  <span className="font-semibold">Project task:</span> {row.project_plan_target}
                                </div>
                              )}
                              {row.pathway_evidence?.description && (
                                <div className="mt-1 text-xs text-gray-700"><span className="font-semibold">Pathway evidence:</span> {row.pathway_evidence.description}</div>
                              )}
                              {row.project_evidence?.description && (
                                <div className="mt-1 text-xs text-gray-700"><span className="font-semibold">Project evidence:</span> {row.project_evidence.description}{row.project_evidence.portfolio_candidate ? ' · Portfolio candidate' : ''}</div>
                              )}
                              {row.plan_success_criteria && (
                                <div className="mt-1 text-xs text-gray-600">
                                  <span className="font-semibold">Show by class end:</span> {row.plan_success_criteria}
                                </div>
                              )}
                              {row.protected_goal_minutes && <div className="mt-1 text-xs text-gray-700"><span className="font-semibold">Protected goal time:</span> {row.protected_goal_minutes} minutes</div>}
                              {row.planned_secondary_work && <div className="mt-1 text-xs text-gray-700"><span className="font-semibold">Then:</span> {row.planned_secondary_work}</div>}
                              {row.planned_work_mode && <div className="mt-1 text-xs text-gray-700"><span className="font-semibold">Planned mode:</span> {row.planned_work_mode.replace(/_/g, ' ')}</div>}
                              {row.focus_state && <div className="mt-1 text-xs font-medium text-indigo-700"><span className="font-semibold">Focus block:</span> {row.focus_state.label}{row.focus_state.block?.title ? ` · ${row.focus_state.block.title}` : ''}{row.focus_state.state === 'active' && row.focus_state.remaining_seconds !== null && row.focus_state.remaining_seconds !== undefined ? ` · ${formatFocusTime(row.focus_state.remaining_seconds)} left` : ''}</div>}
                              {row.support_request && <div className="mt-1 text-xs font-semibold text-rose-700">Help {row.support_request.status === 'acknowledged' ? 'acknowledged; follow-up remains open' : 'requested'}{row.support_request.requested_at ? ` · ${formatDateTime(row.support_request.requested_at)}` : ''}.</div>}
                              {row.plan_change_reason && <div className="mt-1 text-xs text-gray-700"><span className="font-semibold">Plan reflection:</span> {row.plan_change_reason.replace(/_/g, ' ')}{row.plan_change_note ? ` — ${row.plan_change_note}` : ''}</div>}
                              {row.priority_result && (
                                <div className="mt-1 text-xs font-medium text-emerald-700">
                                  Result: {row.priority_result.replace(/_/g, ' ')}
                                </div>
                              )}
                              <div className="mt-1 text-xs font-medium text-gray-700">
                                Main signal: {priorityReasonForRow(row)}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                Missing: {row.missing_total_count} (internal {row.missing_internal_count}, external {row.missing_external_count})
                              </div>
                              {row.primary_goal && (
                                <div className="mt-1 text-xs text-emerald-800">
                                  Goal: {row.primary_goal.title}
                                </div>
                              )}
                              {row.primary_goal?.quality_workflow && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${row.primary_goal.quality_workflow.gate_satisfied ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
                                    {row.primary_goal.goal_purpose === 'technical_cte' ? 'Technical' : row.primary_goal.goal_purpose === 'personal_professional' ? 'Personal/professional' : 'Legacy'} · {row.primary_goal.quality_workflow.state.replace(/_/g, ' ')}
                                  </span>
                                  {row.primary_goal.quality_workflow.standards_status === 'catalog_unavailable' && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">standards catalog unavailable</span>}
                                </div>
                              )}
                              {(row.primary_goal?.practice_standards || []).length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {(row.primary_goal?.practice_standards || []).map((standard) => <span key={standard.id} className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] text-sky-800" title={standard.official_text}>{standard.pathway_key.replace(/_/g, ' ')} · {standard.official_code}</span>)}
                                </div>
                              )}
                              {row.primary_goal && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${row.weekly_checkpoint ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {checkpointLabelForRow(row)}
                                  </span>
                                  {row.followup_triage?.recommended_action && (
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700">
                                      {row.followup_triage.recommended_action}
                                    </span>
                                  )}
                                </div>
                              )}
                              {row.goals_applicable && row.goal_quality && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${goalQualityChipClass(row.goal_quality.learning?.level)}`}>
                                    Learning: {row.goal_quality.learning?.label || 'Missing'}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${goalQualityChipClass(row.goal_quality.completion?.level)}`}>
                                    Completion: {row.goal_quality.completion?.label || 'Missing'}
                                  </span>
                                </div>
                              )}
                              {row.project_work?.has_project_work && (
                                <div className="mt-1 text-xs text-indigo-700">
                                  Project: {row.project_work.role_label || 'project work'}
                                  {row.project_work.selected_title ? ` | ${row.project_work.selected_title}` : ''}
                                  {Number(row.project_work.project_minutes || 0) > 0 ? ` | ${row.project_work.project_minutes}m` : ''}
                                </div>
                              )}
                              {row.goals_applicable && row.external_risks && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {row.external_risks.active_block && (
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">
                                      active block
                                    </span>
                                  )}
                                </div>
                              )}
                              {row.grade_level && (
                                <div className="text-xs text-gray-400">Grade {row.grade_level}</div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${priorityChipClass(priorityLevel)}`}>
                                {priorityLabelForLevel(priorityLevel)}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusChipClass(row.status)}`}>
                                {dailyStatusLabel(row)}
                              </span>
                            </div>
                          </div>
                        </button>
                        {selectedStudentId === row.student_id && row.primary_goal?.goal_purpose === 'technical_cte' && (
                          <div className="border-t border-slate-200 bg-sky-50 p-3 text-xs">
                            <p className="font-semibold text-sky-950">Student standards awareness</p>
                            <p className="mt-1 text-sky-800">These are practice references selected in Elevate. Official standards assessment and records remain in HeadRush.</p>
                            {(row.primary_goal.practice_standards || []).length > 0 ? (
                              <div className="mt-2 space-y-2">
                                {(row.primary_goal.practice_standards || []).map((standard) => (
                                  <div key={standard.id} className="rounded border border-sky-200 bg-white p-2 text-sky-950">
                                    <p className="font-semibold">{standard.content_code}: {standard.content_title}</p>
                                    <p className="mt-1">{standard.official_code} · {standard.performance_title}</p>
                                    <p className="mt-1 text-sky-800">{standard.official_text}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="mt-2 text-sky-800">The student has not selected a practice standard yet.</p>
                            )}
                          </div>
                        )}
                        {selectedStudentId === row.student_id && row.primary_goal && (row.primary_goal.milestones || []).length > 0 && (
                          <div className="mt-3 space-y-2">
                            {(row.primary_goal.milestones || []).map((milestone) => {
                              const editable = Boolean(
                                row.primary_goal?.is_active
                                && !row.primary_goal?.closed_at
                                && row.primary_goal?.instructional_cycle_detail?.state === 'active',
                              );
                              return (
                                <div key={milestone.id} className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
                                  <div className="mb-2 flex flex-wrap items-center gap-2">
                                    <select
                                      className="rounded border border-slate-300 px-2 py-1 font-semibold"
                                      value={milestone.hook_stage || ''}
                                      disabled={!editable}
                                      aria-label={`Hook stage for ${row.student_name}`}
                                      onChange={(event) => void updateTeacherMilestone(row, milestone, { hook_stage: event.target.value as GoalMilestone['hook_stage'] })}
                                    >
                                      <option value="">Other milestone</option><option value="define">DEFINE</option><option value="do">DO</option><option value="explain">EXPLAIN</option><option value="apply">APPLY</option>
                                    </select>
                                    <span>{milestone.linked_evidence_count || 0} linked evidence item{milestone.linked_evidence_count === 1 ? '' : 's'}</span>
                                    <span>HeadRush: {milestone.headrush_status === 'uploaded' ? 'uploaded' : 'not uploaded'}</span>
                                  </div>
                                  <input
                                    className="w-full rounded border border-slate-300 px-2 py-1"
                                    defaultValue={milestone.expected_outcome}
                                    disabled={!editable}
                                    aria-label={`Milestone outcome for ${row.student_name}`}
                                    onBlur={(event) => {
                                      const value = event.target.value.trim();
                                      if (value && value !== milestone.expected_outcome) void updateTeacherMilestone(row, milestone, { expected_outcome: value });
                                    }}
                                  />
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1"
                                      type="date"
                                      defaultValue={milestone.due_date}
                                      disabled={!editable}
                                      aria-label={`Milestone due date for ${row.student_name}`}
                                      onBlur={(event) => {
                                        if (event.target.value && event.target.value !== milestone.due_date) void updateTeacherMilestone(row, milestone, { due_date: event.target.value });
                                      }}
                                    />
                                    <span className="rounded-full bg-white px-2 py-1">{milestone.status.replace(/_/g, ' ')}</span>
                                    <label className="flex items-center gap-1">
                                      <input
                                        type="checkbox"
                                        checked={milestone.evidence_expected}
                                        disabled={!editable}
                                        onChange={(event) => void updateTeacherMilestone(row, milestone, { evidence_expected: event.target.checked })}
                                      />
                                      Evidence expected
                                    </label>
                                  </div>
                                  {milestone.progress_note && <p className="mt-2 text-slate-600">Student note: {milestone.progress_note}</p>}
                                  {(milestone.evidence_description || milestone.evidence_url) && (
                                    <div className="mt-2 rounded border border-indigo-100 bg-white p-2 text-slate-700">
                                      <span className="font-semibold">Evidence:</span> {milestone.evidence_description || 'Linked artifact'}
                                      {milestone.evidence_url && <a className="ml-2 text-indigo-700 underline" href={milestone.evidence_url} target="_blank" rel="noreferrer">Open</a>}
                                    </div>
                                  )}
                                  <div className="mt-2 grid gap-2 sm:grid-cols-[180px_minmax(0,1fr)]">
                                    <select
                                      className="rounded border border-slate-300 px-2 py-1"
                                      value={milestone.verification_status || 'not_submitted'}
                                      disabled={!editable}
                                      aria-label={`Verification status for ${row.student_name}`}
                                      onChange={(event) => void updateTeacherMilestone(row, milestone, { verification_status: event.target.value as GoalMilestone['verification_status'] })}
                                    >
                                      <option value="not_submitted">Not submitted</option><option value="pending">Pending review</option><option value="verified">Verified</option><option value="needs_revision">Needs revision</option>
                                    </select>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1"
                                      defaultValue={milestone.verification_note || ''}
                                      disabled={!editable}
                                      placeholder="Teacher verification note"
                                      aria-label={`Verification note for ${row.student_name}`}
                                      onBlur={(event) => { if (event.target.value.trim() !== (milestone.verification_note || '')) void updateTeacherMilestone(row, milestone, { verification_note: event.target.value.trim() }); }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {selectedStudentId === row.student_id && row.status === 'not_checked_in' && row.goals_applicable && (
                          <div className="mt-3 flex gap-2">
                            <input
                              className="flex-1 border rounded px-2 py-1 text-sm"
                              placeholder="Bypass reason..."
                              value={bypassReasonByStudent[row.student_id] || ''}
                              onChange={(event) => setBypassReasonByStudent((prev) => ({ ...prev, [row.student_id]: event.target.value }))}
                            />
                            <button
                              type="button"
                              className="px-3 py-1 text-sm bg-indigo-600 text-white rounded"
                              onClick={() => handleBypass(row.student_id)}
                            >
                              Bypass
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

        </div>
        </div>
          <div
            ref={studentDetailRef}
            className={selectedStudentId ? 'w-full rounded-xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500' : 'hidden'}
            tabIndex={-1}
            role="region"
            aria-labelledby="student-goal-detail-heading"
          >
            <div className="border-b bg-white px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 id="student-goal-detail-heading" className="font-semibold text-gray-900">Student Goal Detail</h2>
                  {selectedStudentRow && (
                    <>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{selectedStudentRow.student_name}</p>
                      <p className="mt-0.5 text-xs text-slate-600">{priorityReasonForRow(selectedStudentRow)}</p>
                    </>
                  )}
                </div>
                {selectedStudentId ? (
                  <div className="flex flex-wrap gap-2">
                    {(['Previous', 'Next'] as const).map((direction) => {
                      const index = filteredRosterRows.findIndex((row) => row.student_id === selectedStudentId);
                      const row = filteredRosterRows[index + (direction === 'Next' ? 1 : -1)];
                      return <button type="button" key={direction} disabled={!row || Boolean(goalSupportSaving) || goalReviewSaving} className="rounded border px-3 py-1 text-xs disabled:opacity-50" onClick={(event) => row && selectStudentForDetail(row.student_id, event.currentTarget)}>{direction}</button>;
                    })}
                  <button type="button" className="rounded border border-slate-200 px-3 py-1 text-xs text-slate-600" onClick={closeStudentDetail}>
                    ← Class roster
                  </button>
                  </div>
                ) : null}
              </div>
            </div>
            {!selectedStudentId ? (
              <div className="p-4 text-sm text-gray-500">Select Review goal above or choose a student from the current cycle list to open evidence, check-ins, reviews, and other goal tools.</div>
            ) : !studentDetail ? (
              <div className="p-4 text-sm text-gray-500">Loading student detail...</div>
            ) : (
              <div className="flex flex-col gap-4 p-4">
                <div className="rounded border border-indigo-200 bg-indigo-50/50 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Coaching &amp; Performance</div>
                      <p className="mt-1 text-xs text-slate-600">Open the student's five-week coaching record, evidence summary, instructional conversations, reflection, and review status.</p>
                    </div>
                    <Link
                      className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700"
                      to={`/v2/teacher/coaching/sections/${selectedSectionId}/students/${selectedStudentId}${studentDetail.activePrimaryGoal?.instructional_cycle ? `?cycle_id=${studentDetail.activePrimaryGoal.instructional_cycle}` : ''}`}
                    >
                      Open coaching record
                    </Link>
                  </div>
                </div>
                <nav className="flex flex-wrap gap-2" aria-label="Student workspace">
                  {([['today','Today'],['goal','Goal & approval'],['planner','Plan block goal'],['history','History'],['support','Import / support']] as const).map(([value,label]) => <button type="button" key={value} aria-pressed={detailTab === value} className={'rounded border px-3 py-2 text-sm ' + (detailTab === value ? 'bg-indigo-700 text-white' : 'bg-white')} onClick={() => setDetailTab(value)}>{label}</button>)}
                </nav>
                {detailTab === 'planner' && selectedSectionId && selectedStudentId && <GoalPlanner
                  key={`${selectedSectionId}:${selectedStudentId}`} sectionId={selectedSectionId} studentId={selectedStudentId}
                  studentName={selectedStudentRow?.student_name || 'this student'}
                  reportBlocks={instructionalCycles.filter(cycle => cycle.section === selectedSectionId && cycle.cycle_kind === 'performance_period'
                    && (!(cycle.audience_grades || []).length || cycle.audience_grades.includes(String(selectedStudentRow?.grade_level || ''))))}
                  onSaved={() => { void loadStudentDetail(selectedStudentId, selectedSectionId, selectedDate); void loadSectionDaily(selectedSectionId, selectedDate); }} />}
                {detailTab === 'today' && <section className="rounded border p-4" aria-label="Today’s plan and wrap-up">
                  <h3 className="font-semibold">Today’s plan · {selectedDate}</h3>
                  <p className="mt-2">{studentDetail.cycle?.plan_target || 'No daily plan recorded.'}</p>
                  <p className="mt-2 text-sm">First step: {studentDetail.cycle?.plan_first_step || 'Not recorded'}</p>
                  <p className="mt-2 text-sm">Success check: {studentDetail.cycle?.plan_success_criteria || 'Not recorded'}</p>
                  <p className="mt-2 text-xs text-slate-600">{selectedDetailReason} Missing records do not establish whether work happened.</p>
                  {studentDetail.cycle?.status === 'checked_out' && <div className="mt-4 border-t pt-3"><h3 className="font-semibold">Student wrap-up</h3><p>Result: {wrapUpResultLabel(studentDetail.cycle)} · Student-reported</p><p>{studentDetail.cycle.reflection_text}</p><p>Next step: {studentDetail.cycle.reflection_next_step || 'Not recorded'}</p></div>}
                  <button type="button" className="mt-3 rounded border px-3 py-2" onClick={() => { setGoalPlanImportScope('daily_plan_only'); setDetailTab('support'); }}>Support today’s plan</button>
                </section>}
                <section hidden={detailTab !== 'goal'} className="rounded border border-sky-200 bg-sky-50 p-3" aria-label="Teacher goal review">
                  <h3 className="text-sm font-semibold">Teacher goal review</h3>
                  <p className="mt-1 text-xs">Approve an achievable goal or request a specific revision. Students can continue daily work during review. AI suggestions do not approve or reject goals.</p>
                  {goalReviewMessage && <p role="status" className="mt-2 text-sm">{goalReviewMessage}</p>}
                  {studentDetail.activeGoals.length === 0 && <p className="mt-2 text-sm">No active goal to review yet.</p>}
                  {studentDetail.activeGoals.map((goal) => (
                    <div key={goal.id} className="mt-3 rounded border bg-white p-3 text-sm">
                      <p className="font-semibold">{goal.goal_track === 'personal_project' ? 'Personal Project' : 'Main Goal'}: {goal.title}</p>
                      <p className="mt-1">Why: {goal.why_text}</p>
                      <p className="mt-1 whitespace-pre-line">Progress target: {goal.target_metric_text}</p>
                      {goal.quality_workflow?.state === 'approved' ? (
                        <div className="mt-2 text-emerald-800">
                          <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 font-semibold">✓ Approved</span>
                          {goal.quality_workflow.teacher_review && (
                            <p className="mt-1 text-xs">Approved by {goal.quality_workflow.teacher_review.teacher_name} · {formatDateTime(goal.quality_workflow.teacher_review.created_at)}</p>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1">Teacher review: {(goal.quality_workflow?.state || 'awaiting_teacher_review').replace(/_/g, ' ')}</p>
                      )}
                      {goal.quality_workflow?.teacher_review?.note && <p className="mt-1">Last feedback: {goal.quality_workflow.teacher_review.note}</p>}
                      <label className="mt-2 block">Feedback for the student
                        <textarea className="mt-1 w-full rounded border p-2" value={goalReviewNotes[goal.id] || ''} onChange={(event) => setGoalReviewNotes((current) => ({ ...current, [goal.id]: event.target.value }))} placeholder="Required when requesting a revision. Name one concrete next step." />
                      </label>
                      <div className="mt-2 flex gap-2">
                        <button type="button" className="rounded bg-emerald-700 px-3 py-1 text-white disabled:opacity-50" disabled={goalReviewSaving || goal.quality_workflow?.state === 'approved' || !!goal.closed_at || (!!goal.instructional_cycle_detail && goal.instructional_cycle_detail.state !== 'active')} onClick={() => void handleGoalReview(goal, 'approved')}>{goal.quality_workflow?.state === 'approved' ? 'Approved' : 'Approve goal'}</button>
                        <button type="button" className="rounded border border-amber-700 px-3 py-1 disabled:opacity-50" disabled={goalReviewSaving || !!goal.closed_at || (!!goal.instructional_cycle_detail && goal.instructional_cycle_detail.state !== 'active')} onClick={() => void handleGoalReview(goal, 'needs_revision')}>Request revision</button>
                      </div>
                    </div>
                  ))}
                </section>

                <details hidden={detailTab !== 'support'} className="rounded border border-violet-200 bg-violet-50 p-3" open>
                  <summary className="cursor-pointer text-sm font-semibold text-violet-950">Import or support a daily plan / five-week goal</summary>
                  {goalSupportError && <p role="alert" className="mt-2 text-sm text-rose-700">{goalSupportError}</p>}
                  {goalSupportSaved && <p role="status" className="mt-2 text-sm text-emerald-700">{goalSupportSaved}</p>}
                  <p className="mt-2 text-xs text-violet-900">
                    Use this when the student needs teacher help to clarify, adapt, or fully state the work. The reason and resulting text are student-visible.
                  </p>
                  <p className="mt-1 text-xs font-medium text-rose-700">
                    Enter instructional supports only. Do not enter diagnoses, disability/IEP details, health information, or family information here.
                  </p>

                  <div className="mt-4 rounded border border-violet-200 bg-white p-3">
                    <div className="text-sm font-semibold text-slate-900">Import today’s plan or a five-week goal</div>
                    <p className="mt-1 text-xs text-slate-600">
                      Paste labeled text, JSON, or a one-record CSV, or choose a .txt, .json, or .csv file. Choose whether this import may change the five-week goal.
                    </p>
                    <label className="mt-3 block text-xs font-medium text-slate-700">What should this import change?
                      <select
                        aria-label="Import scope"
                        className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal"
                        value={goalPlanImportScope}
                        onChange={(event) => {
                          setGoalPlanImportScope(event.target.value as GoalPlanImportScope);
                          setConfirmGoalChange(false);
                          setGoalPlanImportMessage('');
                          setGoalPlanImportWarnings([]);
                        }}
                      >
                        <option value="daily_plan_only">Today’s plan only — keep the current five-week goal</option>
                        <option value="primary_goal_and_daily_plan">Five-week goal + today’s plan</option>
                        <option value="primary_goal_only">Five-week goal only</option>
                      </select>
                    </label>
                    <p className="mt-1 text-[11px] font-medium text-emerald-700">
                      Today’s plan only is the safe default for recurring support and will ignore any primary-goal fields in the source.
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Daily-plan labels include Daily track, Daily task, First step, Success check, Protected minutes, Work mode, Support level, and Support reason. Add Goal purpose, Goal, Why, and Progress target only when deliberately changing the five-week goal.
                    </p>
                    <label className="mt-3 block text-xs font-medium text-slate-700">Goal and daily plan import
                      <textarea
                        className="mt-1 min-h-[110px] w-full rounded border border-slate-300 p-2 font-mono text-xs font-normal"
                        value={goalPlanImportSource}
                        onChange={(event) => {
                          setGoalPlanImportSource(event.target.value);
                          setGoalPlanImportMessage('');
                          setGoalPlanImportWarnings([]);
                        }}
                        placeholder={'Daily track: comptia\nDaily task: Complete today’s assigned section\nFirst step: Open the lesson and identify the first required item\nSuccess check: Show the completed section and explain one decision\nProtected minutes: 40\nWork mode: live_lesson\nSupport level: teacher_stated\nSupport reason: Today’s work has a clear starting point and finish check.'}
                      />
                    </label>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <label className="cursor-pointer rounded border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800">
                        Choose Import File
                        <input
                          type="file"
                          accept=".txt,.json,.csv,text/plain,application/json,text/csv"
                          className="sr-only"
                          onChange={(event) => {
                            void loadGoalPlanImportFile(event.target.files?.[0]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                      <button type="button" className="rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={!goalPlanImportSource.trim()} onClick={() => loadGoalPlanImportIntoDrafts()}>
                        Load into Review Form
                      </button>
                    </div>
                    {goalPlanImportMessage && <p className="mt-2 text-xs font-medium text-emerald-700">{goalPlanImportMessage}</p>}
                    {goalPlanImportWarnings.length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-700">
                        {goalPlanImportWarnings.map((warning) => <li key={warning}>{warning}</li>)}
                      </ul>
                    )}
                    <p className="mt-2 text-[11px] font-medium text-rose-700">
                      Student names, IDs, birth dates, diagnoses, eligibility, medical/health, IEP, and family fields are ignored. Import a normalized instructional record—not a complete confidential document.
                    </p>
                  </div>

                  <div hidden={goalPlanImportScope === 'daily_plan_only'} className="mt-4 rounded border border-violet-200 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">Primary goal</div>
                      {studentDetail.currentPrimaryGoal?.latest_support && (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] text-violet-800">
                          {studentDetail.currentPrimaryGoal.latest_support.support_mode === 'teacher_stated' ? 'teacher stated' : 'teacher adapted'}
                        </span>
                      )}
                    </div>
                    {studentDetail.currentPrimaryGoal?.latest_support?.student_visible_rationale && (
                      <p className="mt-1 text-xs text-slate-600">Last reason: {studentDetail.currentPrimaryGoal.latest_support.student_visible_rationale}</p>
                    )}
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <label className="text-xs font-medium text-slate-700">Support level
                        <select className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal" value={primarySupportDraft.support_mode} onChange={(event) => setPrimarySupportDraft((current) => ({ ...current, support_mode: event.target.value as GoalSupportMode }))}>
                          <option value="adapted">Adapt or clarify with the student</option>
                          <option value="teacher_stated">Teacher states the complete goal</option>
                        </select>
                      </label>
                      <label className="text-xs font-medium text-slate-700">Goal purpose
                        <select className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal" value={primarySupportDraft.goal_purpose} onChange={(event) => {
                          const goalPurpose = event.target.value as PrimaryGoalSupportDraft['goal_purpose'];
                          setPrimarySupportDraft((current) => ({
                            ...current,
                            goal_purpose: goalPurpose,
                            student_visible_rationale: current.student_visible_rationale || (
                              goalPurpose === 'technical_cte'
                                ? 'We classified this goal as technical / CTE work so it can use the appropriate goal review and Idaho standards process.'
                                : goalPurpose === 'personal_professional'
                                  ? 'We classified this as a personal / professional growth goal so it receives coaching without a technical standards requirement.'
                                  : ''
                            ),
                          }));
                        }}>
                          <option value="">Choose a purpose</option>
                          <option value="technical_cte">Technical / CTE</option>
                          <option value="personal_professional">Personal / professional</option>
                        </select>
                      </label>
                    </div>
                    <label className="mt-2 block text-xs font-medium text-slate-700">Goal
                      <textarea className="mt-1 min-h-[64px] w-full rounded border border-slate-300 p-2 font-normal" maxLength={255} value={primarySupportDraft.title} onChange={(event) => setPrimarySupportDraft((current) => ({ ...current, title: event.target.value }))} />
                    </label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <label className="text-xs font-medium text-slate-700">Why this matters
                        <textarea className="mt-1 min-h-[72px] w-full rounded border border-slate-300 p-2 font-normal" value={primarySupportDraft.why_text} onChange={(event) => setPrimarySupportDraft((current) => ({ ...current, why_text: event.target.value }))} />
                      </label>
                      <label className="text-xs font-medium text-slate-700">Observable progress target
                        <textarea className="mt-1 min-h-[72px] w-full rounded border border-slate-300 p-2 font-normal" value={primarySupportDraft.target_metric_text} onChange={(event) => setPrimarySupportDraft((current) => ({ ...current, target_metric_text: event.target.value }))} />
                      </label>
                    </div>
                    <label className="mt-2 block text-xs font-medium text-slate-700">Student-visible support reason <span className="text-rose-700">(required)</span>
                      <input className="mt-1 w-full rounded border border-slate-300 p-2 font-normal" maxLength={500} placeholder="For example: We broke this goal into clearer, manageable steps." value={primarySupportDraft.student_visible_rationale} onChange={(event) => setPrimarySupportDraft((current) => ({ ...current, student_visible_rationale: event.target.value }))} />
                    </label>
                    <p className="mt-1 text-[11px] text-slate-600">Students can see this explanation. It also records why the teacher changed or classified the goal.</p>
                    <label className="mt-3 block text-sm"><input type="checkbox" checked={confirmGoalChange} onChange={(event) => setConfirmGoalChange(event.target.checked)} /> I intend to update this student’s five-week goal.</label>
                    <button type="button" className="mt-3 rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={goalSupportSaving !== '' || !confirmGoalChange} onClick={() => void savePrimaryGoalSupport()}>
                      {goalSupportSaving === 'primary' ? 'Saving...' : studentDetail.currentPrimaryGoal ? 'Save Goal Support' : 'Create Teacher-Stated Goal'}
                    </button>
                  </div>

                  <div hidden={goalPlanImportScope === 'primary_goal_only'} className="mt-3 rounded border border-violet-200 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">Daily plan · {selectedDate}</div>
                      {studentDetail.cycle?.latest_support && (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] text-violet-800">
                          {studentDetail.cycle.latest_support.support_mode === 'teacher_stated' ? 'teacher stated' : 'teacher adapted'}
                        </span>
                      )}
                    </div>
                    {studentDetail.cycle?.latest_support?.student_visible_rationale && (
                      <p className="mt-1 text-xs text-slate-600">Last reason: {studentDetail.cycle.latest_support.student_visible_rationale}</p>
                    )}
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <label className="text-xs font-medium text-slate-700">Support level
                        <select className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal" value={dailySupportDraft.support_mode} onChange={(event) => setDailySupportDraft((current) => ({ ...current, support_mode: event.target.value as GoalSupportMode }))}>
                          <option value="adapted">Adapt or clarify with the student</option>
                          <option value="teacher_stated">Teacher states the complete plan</option>
                        </select>
                      </label>
                      <label className="text-xs font-medium text-slate-700">Plan track
                        <select className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal" value={dailySupportDraft.plan_track} onChange={(event) => setDailySupportDraft((current) => ({ ...current, plan_track: event.target.value }))}>
                          <option value="">Choose or leave unchanged</option>
                          <option value="python">Python</option><option value="comptia">CompTIA</option><option value="web_development">Web development</option><option value="main_project">Main project</option><option value="secondary_project">Secondary project</option><option value="missing_work">Missing work</option><option value="other">Other / custom</option>
                        </select>
                      </label>
                    </div>
                    <label className="mt-2 block text-xs font-medium text-slate-700">Today’s task
                      <input className="mt-1 w-full rounded border border-slate-300 p-2 font-normal" maxLength={500} value={dailySupportDraft.plan_target} onChange={(event) => setDailySupportDraft((current) => ({ ...current, plan_target: event.target.value }))} />
                    </label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <label className="text-xs font-medium text-slate-700">First step
                        <textarea className="mt-1 min-h-[64px] w-full rounded border border-slate-300 p-2 font-normal" maxLength={500} value={dailySupportDraft.plan_first_step} onChange={(event) => setDailySupportDraft((current) => ({ ...current, plan_first_step: event.target.value }))} />
                      </label>
                      <label className="text-xs font-medium text-slate-700">Success check
                        <textarea className="mt-1 min-h-[64px] w-full rounded border border-slate-300 p-2 font-normal" maxLength={500} value={dailySupportDraft.plan_success_criteria} onChange={(event) => setDailySupportDraft((current) => ({ ...current, plan_success_criteria: event.target.value }))} />
                      </label>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <label className="text-xs font-medium text-slate-700">Protected goal minutes
                        <select className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal" value={dailySupportDraft.protected_goal_minutes} onChange={(event) => setDailySupportDraft((current) => ({ ...current, protected_goal_minutes: event.target.value }))}>
                          <option value="">Not set</option>{[5, 10, 15, 20, 25, 30, 40, 45, 60, 90].map((minutes) => <option key={minutes} value={minutes}>{minutes} minutes</option>)}
                        </select>
                      </label>
                      <label className="text-xs font-medium text-slate-700">Likely work mode
                        <select className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-normal" value={dailySupportDraft.planned_work_mode} onChange={(event) => setDailySupportDraft((current) => ({ ...current, planned_work_mode: event.target.value }))}>
                          <option value="">Not set</option><option value="five_week_goal">Five-week goal</option><option value="nerd_herd">Nerd Herd service</option><option value="live_lesson">Teacher live lesson</option><option value="personal_project">Personal project</option><option value="required_recovery">Required / recovery work</option><option value="exploration">Exploration</option><option value="transition_cleanup">Transition / cleanup</option>
                        </select>
                      </label>
                    </div>
                    <label className="mt-2 block text-xs font-medium text-slate-700">What comes next (optional)
                      <input className="mt-1 w-full rounded border border-slate-300 p-2 font-normal" maxLength={500} value={dailySupportDraft.planned_secondary_work} onChange={(event) => setDailySupportDraft((current) => ({ ...current, planned_secondary_work: event.target.value }))} />
                    </label>
                    <label className="mt-2 block text-xs font-medium text-slate-700">Student-visible support reason
                      <input className="mt-1 w-full rounded border border-slate-300 p-2 font-normal" maxLength={500} placeholder="For example: We chunked today’s work and added a clear first step." value={dailySupportDraft.student_visible_rationale} onChange={(event) => setDailySupportDraft((current) => ({ ...current, student_visible_rationale: event.target.value }))} />
                    </label>
                    <button type="button" className="mt-3 rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={goalSupportSaving !== '' || studentDetail.cycle?.status === 'checked_out' || selectedStudentRow?.is_school_day === false} onClick={() => void saveDailyPlanSupport()}>
                      {goalSupportSaving === 'daily' ? 'Saving...' : 'Save Daily Plan Support'}
                    </button>
                    {studentDetail.cycle?.status === 'checked_out' && <p className="mt-2 text-xs text-slate-500">Checked-out plans are preserved as history and cannot be changed.</p>}
                  </div>
                </details>

                <details hidden={detailTab !== 'history'} open className="rounded border border-slate-200 bg-slate-50">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800">
                    Goal history and prior daily cycles
                  </summary>
                  <div className="space-y-2 border-t border-slate-200 p-3">
                    {studentDetail.history.length === 0 ? (
                      <p className="text-sm text-slate-500">No prior goal cycles are available.</p>
                    ) : studentDetail.history.map((cycle) => (
                      <div key={cycle.id} className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-600">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-semibold text-slate-900">{cycle.local_school_date}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5">{cycle.status.replace(/_/g, ' ')}</span>
                        </div>
                        <p className="mt-1">{cycle.plan_target || `${cycle.goals.length} goal(s) recorded`}</p>
                        {cycle.reflection_text && <p className="mt-2">Wrap-up: {cycle.reflection_text}</p>}
                        {cycle.reflection_next_step && <p className="mt-1">Next step: {cycle.reflection_next_step}</p>}
                      </div>
                    ))}
                  </div>
                </details>

                <div hidden={detailTab !== 'today'}>
                  <div className="text-sm font-medium text-gray-800">Daily feedback</div>
                  <p className="text-xs text-slate-600">Visible to the student. Keep private notes in Check-ins &amp; performance.</p>
                  {!studentDetail.cycle && <p className="mt-2 text-sm text-slate-600">Daily feedback is available after a daily record exists. Use the coaching record for a conversation now.</p>}
                  <div className="mt-2 space-y-2 max-h-32 overflow-auto">
                    {dailyComments.length === 0 ? (
                      <div className="text-sm text-gray-500">No comments yet.</div>
                    ) : dailyComments.map((comment) => (
                      <div key={comment.id} className="border rounded p-2">
                        <div className="text-xs text-gray-500">
                          {comment.teacher_name} | {new Date(comment.created_at).toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-800">{comment.text}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <input
                      className="flex-1 border rounded px-2 py-1 text-sm"
                      placeholder="Add coaching comment..."
                      aria-label="Daily feedback for the student"
                      disabled={!studentDetail.cycle}
                      value={commentText}
                      onChange={(event) => setCommentText(event.target.value)}
                    />
                    <button
                      type="button"
                      className="px-3 py-1 text-sm bg-indigo-600 text-white rounded"
                      onClick={handleComment}
                      disabled={!studentDetail.cycle || !commentText.trim()}
                    >
                      Comment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
      </div>
    </OptionalAppShell>
  );
};
